import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import moment from "moment";
import { postAddDeskDetails, getDeskDetailsAgainstUserName, updateBreakTime } from "../../Service/AllActivityTrackerAPI";

const ActivityTrackerContext = createContext(null);

// Keys for localStorage persistence
const STORAGE_STATE_KEY = "PETROPULSE_EMPLOYEE_TRACKER_STATE";
const STORAGE_ATTENDANCE_KEY = "PETROPULSE_ATTENDANCE_HISTORY";
const STORAGE_BREAKS_KEY = "PETROPULSE_BREAK_HISTORY";
const STORAGE_LOGS_KEY = "PETROPULSE_ACTIVITY_LOGS";
const STORAGE_LAST_SHIFT_KEY = "PETROPULSE_LAST_COMPLETED_SHIFT";
export const STORAGE_EXPLICIT_END_KEY = "PETROPULSE_SHIFT_EXPLICITLY_ENDED";

// Helper to get currently logged in username from sessionStorage or localStorage
export const getCurrentLoggedInUsername = () => {
  if (typeof window === "undefined") return "guest_user";
  try {
    const fromSession = sessionStorage.getItem("userName");
    if (fromSession && fromSession.trim() && fromSession !== "null" && fromSession !== "undefined") {
      return fromSession.trim().toLowerCase();
    }
    const fromLocal = localStorage.getItem("userName");
    if (fromLocal && fromLocal.trim() && fromLocal !== "null" && fromLocal !== "undefined") {
      return fromLocal.trim().toLowerCase();
    }
    const pkUserId = sessionStorage.getItem("pkUserId") || localStorage.getItem("pkUserId");
    if (pkUserId && pkUserId.trim() && pkUserId !== "null" && pkUserId !== "undefined") {
      return pkUserId.trim().toLowerCase();
    }
  } catch (e) { }
  return "guest_user";
};

// Helper to scope any localStorage key by the currently logged in user
export const getUserScopedStorageKey = (baseKey, customUsername = null) => {
  const username = customUsername || getCurrentLoggedInUsername();
  const cleanUsername = String(username || "guest_user").trim().toLowerCase().replace(/[^a-zA-Z0-9_.-]/g, "_");
  return `${baseKey}_${cleanUsername}`;
};

// Remove obsolete legacy global (unscoped) storage keys from previous versions
export const cleanupLegacyGlobalKeys = () => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const legacyKeys = [
      "PETROPULSE_EMPLOYEE_TRACKER_STATE",
      "PETROPULSE_ATTENDANCE_HISTORY",
      "PETROPULSE_BREAK_HISTORY",
      "PETROPULSE_ACTIVITY_LOGS",
      "PETROPULSE_LAST_COMPLETED_SHIFT",
      "PETROPULSE_SHIFT_EXPLICITLY_ENDED",
    ];
    legacyKeys.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch (e) { }
    });
  } catch (e) { }
};

// Purge obsolete/cached mock Activity Tracker data while safely preserving active session keys
export const removeAllActivityTrackerLocalStorage = (preserveActiveSession = true) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const currentUsername = getCurrentLoggedInUsername();
    const activeStateKey = getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername);
    const activeExplicitEndKey = getUserScopedStorageKey(STORAGE_EXPLICIT_END_KEY, currentUsername);
    const activeLastShiftKey = getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, currentUsername);

    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      // Preserve user authentication & session credentials
      if (
        key === "userName" ||
        key === "pkUserId" ||
        key === "userId" ||
        key === "token" ||
        key === "firstName" ||
        key === "lastName" ||
        key === "role" ||
        key === "designation"
      ) {
        continue;
      }
      // Preserve active session if requested
      if (
        preserveActiveSession &&
        (key === activeStateKey || key === activeExplicitEndKey || key === activeLastShiftKey)
      ) {
        continue;
      }

      const upper = key.toUpperCase();
      if (
        key.startsWith("PETROPULSE_") ||
        upper.includes("PETROPULSE") ||
        upper.includes("ATTENDANCE") ||
        upper.includes("TRACKER") ||
        upper.includes("LAST_SHIFT") ||
        upper.includes("LAST_COMPLETED_SHIFT") ||
        upper.includes("EXPLICIT_END") ||
        upper.includes("BREAK_HISTORY") ||
        upper.includes("ACTIVITY_LOGS") ||
        upper.includes("PROJECTS") ||
        upper.includes("TASKS")
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch (e) { }
    });
  } catch (e) { }
};

// Helper to format seconds into HH:MM:SS (handles numbers, seconds, or strings like "0:5:1")
export const formatDuration = (totalSec) => {
  if (totalSec === null || totalSec === undefined || totalSec === "" || totalSec === "null") return "00:00:00";
  if (typeof totalSec === "string" && totalSec.includes(":")) {
    const parts = totalSec.split(":").map((p) => String(p).trim().padStart(2, "0"));
    if (parts.length === 3) return `${parts[0]}:${parts[1]}:${parts[2]}`;
    if (parts.length === 2) return `00:${parts[0]}:${parts[1]}`;
  }
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const hrs = Math.floor(sec / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  const remainingSec = sec % 60;
  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(remainingSec).padStart(2, "0")}`;
};

// Helper to format seconds into human readable e.g. "5h 44m" or "2h 15m 30s"
export const formatDurationHuman = (totalSec) => {
  const sec = Math.max(0, Math.floor(totalSec || 0));
  const hrs = Math.floor(sec / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  const remainingSec = sec % 60;
  if (hrs > 0) {
    return remainingSec > 0 ? `${hrs}h ${mins}m ${remainingSec}s` : `${hrs}h ${mins}m`;
  }
  if (mins > 0) {
    return remainingSec > 0 ? `${mins}m ${remainingSec}s` : `${mins}m`;
  }
  return `${remainingSec}s`;
};

// Helper to format seconds into full words e.g. "5 hours 44 mins" or "1 hour"
export const formatDurationWords = (totalSec) => {
  const sec = Math.max(0, Math.floor(totalSec || 0));
  const hrs = Math.floor(sec / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  const remainingSec = sec % 60;
  if (hrs > 0) {
    const hrStr = hrs === 1 ? "1 hour" : `${hrs} hours`;
    if (mins > 0) {
      const minStr = mins === 1 ? "1 min" : `${mins} mins`;
      return `${hrStr} ${minStr}`;
    }
    return hrStr;
  }
  if (mins > 0) {
    return mins === 1 ? "1 min" : `${mins} mins`;
  }
  return `${remainingSec}s`;
};

// Helper to parse screenactivetime from server API or local state into integer seconds
// - API sends numeric minutes (e.g. "8" -> 8 mins = 480s, "15" -> 15 mins = 900s, "60" -> 60 mins = 3600s, "171" -> 171 mins = 10,260s)
// - Supports HH:MM:SS or MM:SS ("02:51:38", "01:00:00")
// - Supports textual strings ("2 hours 51 mins", "1 hour", "30 mins")
// - Supports values already in seconds (> 1440)
export const parseScreenActiveTimeToSeconds = (val) => {
  if (val === null || val === undefined || val === "" || val === "0" || val === 0 || val === "00:00:00" || val === "00:00") {
    return 0;
  }
  const str = String(val).trim();
  if (!str || str === "0") return 0;

  // Check HH:MM:SS or MM:SS
  if (str.includes(":")) {
    const parts = str.split(":").map((p) => Number(p.trim()));
    if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
      return Math.max(0, parts[0] * 3600 + parts[1] * 60 + parts[2]);
    }
    if (parts.length === 2 && parts.every((n) => !isNaN(n))) {
      return Math.max(0, parts[0] * 60 + parts[1]);
    }
  }

  // Combined words like "2 hours 15 mins", "1 hour 30 min"
  const combinedMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)\s*(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m)s?$/i);
  if (combinedMatch) {
    const hrs = parseFloat(combinedMatch[1]) || 0;
    const mins = parseFloat(combinedMatch[2]) || 0;
    return Math.max(0, Math.round(hrs * 3600 + mins * 60));
  }

  // Handle words like "1 hour", "2 hrs"
  const hrMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:hour|hr|h)s?$/i);
  if (hrMatch) {
    return Math.max(0, Math.round(parseFloat(hrMatch[1]) * 3600));
  }

  // Handle words like "60 mins", "8 min"
  const minMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:minute|min|m)s?$/i);
  if (minMatch) {
    return Math.max(0, Math.round(parseFloat(minMatch[1]) * 60));
  }

  const num = Number(str);
  if (!isNaN(num)) {
    // If num <= 1440 (meaning up to 24 hours in a single day), API sent screenactivetime in MINUTES!
    if (num <= 1440) {
      return Math.max(0, Math.round(num * 60));
    }
    // If num > 1440, it is already in seconds (e.g. 3600, 10260)
    return Math.max(0, Math.floor(num));
  }

  return 0;
};

// Helper to parse break duration from server API or local state into integer seconds
// Supports:
// - "60" -> 60 minutes = 3600 seconds (1 hour)
// - "30" -> 30 minutes = 1800 seconds
// - "15" -> 15 minutes = 900 seconds
// - "01:00:00", "00:30:00", "00:15:00", "00:02:00"
// - Values already in seconds (> 480, e.g. 900, 1800, 3600)
export const parseBreakTimeToSeconds = (val) => {
  if (val === null || val === undefined || val === "" || val === "0" || val === 0 || val === "00:00:00" || val === "00:00") {
    return 0;
  }
  const str = String(val).trim();
  if (!str || str === "0") return 0;

  // Check HH:MM:SS or MM:SS
  if (str.includes(":")) {
    const parts = str.split(":").map((p) => Number(p.trim()));
    if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
      return Math.max(0, parts[0] * 3600 + parts[1] * 60 + parts[2]);
    }
    if (parts.length === 2 && parts.every((n) => !isNaN(n))) {
      return Math.max(0, parts[0] * 60 + parts[1]);
    }
  }

  // Handle combined words like "1 hour 15 mins", "1 hr 30 min", "2 hours 10 mins"
  const combinedMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)\s*(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m)s?$/i);
  if (combinedMatch) {
    const hrs = parseFloat(combinedMatch[1]) || 0;
    const mins = parseFloat(combinedMatch[2]) || 0;
    return Math.max(0, Math.round(hrs * 3600 + mins * 60));
  }

  // Handle words like "1 hour", "1 hr", "30 mins", "15 min"
  const hrMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:hour|hr|h)s?$/i);
  if (hrMatch) {
    return Math.max(0, Math.round(parseFloat(hrMatch[1]) * 3600));
  }
  const minMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:minute|min|m)s?$/i);
  if (minMatch) {
    return Math.max(0, Math.round(parseFloat(minMatch[1]) * 60));
  }

  const num = Number(str);
  if (!isNaN(num)) {
    // If num <= 480 (meaning up to 8 hours), it represents MINUTES from API (60 = 1 hour, 30 = 30 min, 15 = 15 min)
    if (num <= 480) {
      return Math.max(0, Math.round(num * 60));
    }
    // If num > 480, it is already in seconds (e.g. 900, 1800, 3600)
    return Math.max(0, Math.floor(num));
  }

  return 0;
};

// Helper to format break duration into friendly text (e.g. "1 hour", "30 mins", "15 mins", "0 mins")
export const formatBreakTimeMinutes = (val) => {
  if (val === null || val === undefined || val === "" || val === "0" || val === 0 || val === "00:00:00" || val === "00:00") {
    return "0 mins";
  }

  const sec = parseBreakTimeToSeconds(val);
  if (sec <= 0) return "0 mins";

  const totalMins = Math.round(sec / 60);
  if (totalMins <= 0) return "0 mins";

  if (totalMins < 60) {
    return totalMins === 1 ? "1 min" : `${totalMins} mins`;
  }

  const hrs = Math.floor(totalMins / 60);
  const remMins = totalMins % 60;
  const hrLabel = hrs === 1 ? "1 hour" : `${hrs} hours`;

  if (remMins === 0) {
    return hrLabel;
  }

  return `${hrLabel} ${remMins} ${remMins === 1 ? "min" : "mins"}`;
};

// Format timestamp to localized time string e.g. "09:30:15 AM"
export const formatTimeStr = (timestamp) => {
  if (!timestamp) return "--:--:--";
  const d = new Date(timestamp);
  return d.toLocaleTimeString("en-US", { hour12: true });
};

// Format timestamp to localized date string e.g. "15 Oct 2026"
export const formatDateStr = (timestamp) => {
  if (!timestamp) return "--";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

// Helper to get ISO date string YYYY-MM-DD
export const getIsoDateStr = (dateInput = Date.now()) => {
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to check if employee explicitly ended their shift today (persistent across reloads & normal logouts)
export const isShiftExplicitlyEndedToday = (customUsername = null) => {
  try {
    cleanupLegacyGlobalKeys();
    const todayIso = getIsoDateStr(Date.now());
    const user = customUsername || getCurrentLoggedInUsername();
    const key = getUserScopedStorageKey(STORAGE_EXPLICIT_END_KEY, user);
    const val = localStorage.getItem(key);
    if (val === todayIso) return true;

    // Check user-scoped state snapshot
    const stateKey = getUserScopedStorageKey(STORAGE_STATE_KEY, user);
    const savedState = localStorage.getItem(stateKey);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (parsedState?.inactiveReason === "SHIFT_ENDED" && !parsedState?.isShiftActive) {
          const shiftDate = parsedState?.lastEndedShift?.isoDate || getIsoDateStr(parsedState?.sessionStartTime);
          if (shiftDate === todayIso) {
            // Phantom record check: do not treat locally synthesized "Recorded" logout as ended if no real server desk record
            if (parsedState?.lastEndedShift?.logoutTime === "Recorded" && !parsedState?.lastEndedShift?.rawRecord) {
              return false;
            }
            return true;
          }
        }
      } catch (e) { }
    }

    // Check last completed shift record
    const lastKey = getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, user);
    const savedLast = localStorage.getItem(lastKey);
    if (savedLast) {
      try {
        const parsedLast = JSON.parse(savedLast);
        if (parsedLast && (parsedLast.isoDate === todayIso || parsedLast.date === formatDateStr(Date.now())) && parsedLast.status === "Completed") {
          // Phantom record check: do not treat locally synthesized "Recorded" logout as ended if no real server desk record
          if (parsedLast.logoutTime === "Recorded" && !parsedLast.rawRecord) {
            return false;
          }
          return true;
        }
      } catch (e) { }
    }

    return false;
  } catch (e) {
    return false;
  }
};

// Helper to format timestamp into ISO-8601 with local timezone offset (e.g. 2026-10-21T09:00:00+05:30)
export const formatIsoWithOffset = (dateInput = Date.now()) => {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return new Date().toISOString();

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");

  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0");
  const offsetMins = String(absOffset % 60).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
};

// Helper to convert any date representation to YYYY-MM-DD
export const normalizeToIsoDate = (val) => {
  if (!val) return "";
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return val;
  }
  if (typeof val === "number") {
    return getIsoDateStr(val);
  }
  const parsed = Date.parse(val);
  if (!isNaN(parsed)) {
    return getIsoDateStr(parsed);
  }
  return "";
};

// Helper to check if a record matches a date filter / duration range
export const isDateInRange = (recordDateVal, filterType, customDate, startDate, endDate) => {
  const recordIso = normalizeToIsoDate(recordDateVal);
  if (!recordIso) return false;

  const todayIso = getIsoDateStr(Date.now());

  if (filterType === "ALL" || !filterType) return true;

  if (filterType === "TODAY") {
    return recordIso === todayIso;
  }

  if (filterType === "YESTERDAY") {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return recordIso === getIsoDateStr(y);
  }

  if (filterType === "LAST_7_DAYS") {
    const d7 = new Date();
    d7.setDate(d7.getDate() - 6);
    const startIso = getIsoDateStr(d7);
    return recordIso >= startIso && recordIso <= todayIso;
  }

  if (filterType === "LAST_30_DAYS") {
    const d30 = new Date();
    d30.setDate(d30.getDate() - 29);
    const startIso = getIsoDateStr(d30);
    return recordIso >= startIso && recordIso <= todayIso;
  }

  if (filterType === "THIS_MONTH") {
    const dMonth = new Date();
    dMonth.setDate(1);
    const startIso = getIsoDateStr(dMonth);
    return recordIso >= startIso && recordIso <= todayIso;
  }

  if (filterType === "SINGLE_DATE" || filterType === "CUSTOM") {
    return recordIso === customDate;
  }

  if (filterType === "CUSTOM_RANGE") {
    if (!startDate && !endDate) return true;
    if (startDate && endDate) {
      return recordIso >= startDate && recordIso <= endDate;
    }
    if (startDate) return recordIso >= startDate;
    if (endDate) return recordIso <= endDate;
  }

  return recordIso === filterType;
};

// Helper to get standard morning shift start timestamp (always 09:00:00 AM)
export const getMorningShiftStartTime = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(9, 0, 0, 0); // Always morning 09:00:00 AM
  return d.getTime();
};

// Daily shift auto-cutoff at 6 PM removed: shifts continue until employee manually ends shift
export const getShiftCutoffTime = () => {
  return null;
};

// Helper to parse duration string (e.g. "0:5:1", "00:05:01", "300", or seconds) into integer seconds
export const parseDurationToSeconds = (val) => {
  if (!val || val === "0" || val === 0) return 0;
  if (typeof val === "number") return Math.max(0, Math.floor(val));
  const str = String(val).trim();
  if (!str || str === "0") return 0;
  if (/^\d+$/.test(str)) return Number(str);
  const parts = str.split(":").map((p) => Number(p.trim()));
  if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
    return Math.max(0, parts[0] * 3600 + parts[1] * 60 + parts[2]);
  }
  if (parts.length === 2 && parts.every((n) => !isNaN(n))) {
    return Math.max(0, parts[0] * 60 + parts[1]);
  }
  return 0;
};

// List of recognized date & time formats for robust server parsing across timezones
const MOMENT_FORMATS = [
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD hh:mm:ss A",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD hh:mm A",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "DD-MM-YYYY HH:mm:ss",
  "DD-MM-YYYY hh:mm:ss A",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY hh:mm:ss A",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "DD/MM/YYYY",
  "HH:mm:ss",
  "hh:mm:ss A",
  "HH:mm",
  "hh:mm A",
];

// Helper to safely parse dates in various formats
export const parseDateSafe = (val, fallbackOffset = null) => {
  if (!val || val === "0" || val === 0) return null;
  const str = String(val).trim();
  if (!str || str === "0" || str === "--" || str === "null" || str === "undefined") return null;

  if (val instanceof Date && !isNaN(val.getTime())) return val;

  const m = moment(str, MOMENT_FORMATS);
  if (m.isValid()) {
    return m.toDate();
  }

  if (fallbackOffset && !str.match(/([+-]\d{2}:\d{2}|Z)$/)) {
    const dOffset = new Date(str.replace(" ", "T") + fallbackOffset);
    if (!isNaN(dOffset.getTime())) return dOffset;
  }

  const d = new Date(str.replace(" ", "T"));
  if (!isNaN(d.getTime())) return d;

  const dDirect = new Date(str);
  if (!isNaN(dDirect.getTime())) return dDirect;

  return null;
};

// Helper to convert server desk record from /User/getdeskdetailsAgainstUserName into attendance table format
export const transformServerDeskRecord = (item, index = 0) => {
  if (!item) return null;

  const rawLogin =
    item.desklogintime || item.loginTime || item.login_time || item.desk_login_time;
  const rawLogout =
    item.desklogouttime || item.logoutTime || item.logout_time || item.desk_logout_time;
  const rawDateTime =
    item.dateandtime || item.date_and_time || item.createdAt || item.createdDate;

  const offsetMatch = rawLogin ? String(rawLogin).match(/([+-]\d{2}:\d{2}|Z)$/) : null;
  const fallbackOffset = offsetMatch ? offsetMatch[1] : null;

  const loginDate = parseDateSafe(rawLogin) || parseDateSafe(rawDateTime);
  const logoutDate = parseDateSafe(rawLogout, fallbackOffset);

  const hasLogout =
    rawLogout &&
    rawLogout !== "0" &&
    String(rawLogout).trim() !== "" &&
    String(rawLogout).trim() !== "0";

  const baseTs = loginDate ? loginDate.getTime() : Date.now();
  const dateStr = loginDate ? formatDateStr(baseTs) : formatDateStr(Date.now());
  const isoDate = loginDate ? getIsoDateStr(baseTs) : getIsoDateStr(Date.now());
  const loginTimeFormatted = loginDate ? formatTimeStr(baseTs) : (rawLogin && rawLogin !== "0" && String(rawLogin).trim() !== "" ? String(rawLogin) : "--");
  const logoutTimeFormatted = logoutDate ? formatTimeStr(logoutDate.getTime()) : (hasLogout ? String(rawLogout) : "--");

  // Durations from server record
  const rawScreenLock =
    item.desktotalScreenlocktime !== undefined && item.desktotalScreenlocktime !== null
      ? item.desktotalScreenlocktime
      : item.desktotalscreenlocktime !== undefined && item.desktotalscreenlocktime !== null
        ? item.desktotalscreenlocktime
        : item.screenlocktime !== undefined && item.screenlocktime !== null
          ? item.screenlocktime
          : item.screenLockTime !== undefined && item.screenLockTime !== null
            ? item.screenLockTime
            : "0";

  const rawIdle =
    item.desktotalidletime !== undefined && item.desktotalidletime !== null
      ? item.desktotalidletime
      : item.desktotal_idletime !== undefined && item.desktotal_idletime !== null
        ? item.desktotal_idletime
        : item.desktotalIdleTime !== undefined && item.desktotalIdleTime !== null
          ? item.desktotalIdleTime
          : item.deskTotalIdleTime !== undefined && item.deskTotalIdleTime !== null
            ? item.deskTotalIdleTime
            : item.idletime !== undefined && item.idletime !== null
              ? item.idletime
              : item.idleTime !== undefined && item.idleTime !== null
                ? item.idleTime
                : item.idle_time !== undefined && item.idle_time !== null
                  ? item.idle_time
                  : item.desktotalidle !== undefined && item.desktotalidle !== null
                    ? item.desktotalidle
                    : item.inactivetime !== undefined && item.inactivetime !== null
                      ? item.inactivetime
                      : item.inactiveTime !== undefined && item.inactiveTime !== null
                        ? item.inactiveTime
                        : "0";

  const rawBreak =
    item.breaktime !== undefined && item.breaktime !== null
      ? item.breaktime
      : item.break_time !== undefined && item.break_time !== null
        ? item.break_time
        : item.breakTime !== undefined && item.breakTime !== null
          ? item.breakTime
          : "0";

  const screenLockSec = parseDurationToSeconds(rawScreenLock);
  const inactiveSec = parseDurationToSeconds(rawIdle);
  const breakSec = parseBreakTimeToSeconds(rawBreak);

  // Direct screen active time from API / desktop companion agent record
  const rawScreenActive =
    item.screenactivetime !== undefined && item.screenactivetime !== null
      ? item.screenactivetime
      : item.screenActiveTime !== undefined && item.screenActiveTime !== null
        ? item.screenActiveTime
        : item.screen_active_time !== undefined && item.screen_active_time !== null
          ? item.screen_active_time
          : item.desktotalscreenactivetime !== undefined && item.desktotalscreenactivetime !== null
            ? item.desktotalscreenactivetime
            : item.desktotalScreenactivetime !== undefined && item.desktotalScreenactivetime !== null
              ? item.desktotalScreenactivetime
              : item.desktotalactiveworktime !== undefined && item.desktotalactiveworktime !== null
                ? item.desktotalactiveworktime
                : item.activetime !== undefined && item.activetime !== null
                  ? item.activetime
                  : item.activeTime !== undefined && item.activeTime !== null
                    ? item.activeTime
                    : item.active_time !== undefined && item.active_time !== null
                      ? item.active_time
                      : null;

  const todayIso = getIsoDateStr(Date.now());
  const nowTs = Date.now();

  // Dynamic active work time: STRICTLY from response screenactivetime only (parsed as minutes from API)
  const finalActive = rawScreenActive !== null && rawScreenActive !== undefined
    ? parseScreenActiveTimeToSeconds(rawScreenActive)
    : 0;

  let computedLogoutFormatted = logoutTimeFormatted;
  let totalElapsed = 0;

  if (loginDate && logoutDate) {
    totalElapsed = Math.max(0, Math.floor((logoutDate.getTime() - loginDate.getTime()) / 1000));
  } else if (loginDate) {
    if (isoDate !== todayIso) {
      computedLogoutFormatted = "--";
      totalElapsed = Math.max(0, finalActive + inactiveSec + screenLockSec + breakSec);
    } else {
      totalElapsed = Math.max(0, Math.floor((nowTs - loginDate.getTime()) / 1000));
      computedLogoutFormatted = "--";
    }
  }

  // Total shift duration: strictly elapsed time from login until logout (or now if active)
  let totalSec = totalElapsed > 0 ? totalElapsed : (finalActive + inactiveSec + screenLockSec + breakSec);

  // Status computation
  let status = "Completed";
  if (!hasLogout) {
    if (isoDate === todayIso) {
      status = "In Progress";
    } else {
      status = totalSec < 14400 && totalSec > 0 ? "Half-Day" : "Completed";
    }
  } else if (totalSec < 14400 && totalSec > 0) {
    status = "Half-Day";
  }

  const displayId = item.id || item.pkDeskId ? `ATT-${String(item.id || item.pkDeskId).slice(-4).toUpperCase()}` : `ATT-${1000 + index}`;

  return {
    id: String(item.id || item.pkDeskId || displayId),
    displayId,
    pkDeskId: item.pkDeskId || item.id,
    deskusername: item.deskusername || item.username || "",
    date: dateStr,
    isoDate,
    timestamp: baseTs,
    loginTime: loginTimeFormatted,
    logoutTime: computedLogoutFormatted,
    screenLockSeconds: screenLockSec,
    screenLockTime: formatDuration(rawScreenLock || screenLockSec),
    idleSeconds: inactiveSec,
    idleTime: formatDuration(rawIdle || inactiveSec),
    inactiveSeconds: inactiveSec,
    breakSeconds: breakSec,
    breakTime: formatBreakTimeMinutes(rawBreak || breakSec),
    screenActiveTime: finalActive,
    screenactivetime: String(rawScreenActive !== null && rawScreenActive !== undefined ? rawScreenActive : finalActive),
    activeSeconds: finalActive,
    totalSeconds: totalSec,
    desktotalScreenlocktime: String(rawScreenLock || screenLockSec || "0"),
    desktotalidletime: String(rawIdle || inactiveSec || "0"),
    breaktime: String(rawBreak || breakSec || "0"),
    status,
    rawRecord: item,
  };
};

// Retrieve initial attendance records (starts empty; populated dynamically from server API without fallback dummy data)
const getInitialAttendance = (customUsername = null) => {
  cleanupLegacyGlobalKeys();
  return [];
};

// Retrieve break history (starts empty, only records real breaks during session, no dummy data)
const getInitialBreaks = () => {
  return [];
};

// Retrieve activity logs (starts empty, only records real events during session, no dummy data)
const getInitialLogs = () => {
  return [];
};

export const ActivityTrackerProvider = ({ children }) => {
  // Current employee information from session storage or fallback
  const [employee, setEmployee] = useState(() => {
    const firstName = sessionStorage.getItem("firstName") || "";
    const lastName = sessionStorage.getItem("lastName") || "";
    const pkUserId = sessionStorage.getItem("pkUserId") || sessionStorage.getItem("userId") || "";
    const role = sessionStorage.getItem("designation") || sessionStorage.getItem("role") || "Employee";
    const userName = sessionStorage.getItem("userName") || "";

    const name = [firstName, lastName].filter(Boolean).join(" ") || userName || "Employee";

    return {
      id: pkUserId || userName || "Employee",
      name,
      firstName: firstName || name,
      lastName: lastName || "",
      role,
      userName,
      username: userName,
    };
  });

  const employeeRef = useRef(employee);
  useEffect(() => {
    employeeRef.current = employee;
  }, [employee]);

  // Test mode flag: true = 30 sec idle timeout for rapid demo/testing; false = 10 minutes production
  const [isTestMode, setIsTestMode] = useState(false);

  const getSavedSession = (customUsername = null) => {
    try {
      cleanupLegacyGlobalKeys();
      const currentUsername = customUsername || getCurrentLoggedInUsername();
      const key = getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername);
      const lastKey = getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, currentUsername);
      const explicitKey = getUserScopedStorageKey(STORAGE_EXPLICIT_END_KEY, currentUsername);
      const todayIso = getIsoDateStr(Date.now());
      const todayDateStr = formatDateStr(Date.now());

      // Auto-cleanup stale explicit end marker from previous dates
      const explicitVal = localStorage.getItem(explicitKey);
      if (explicitVal && explicitVal !== todayIso) {
        localStorage.removeItem(explicitKey);
      }

      // Auto-cleanup stale last completed shift from previous dates
      const lastSaved = localStorage.getItem(lastKey);
      if (lastSaved) {
        try {
          const parsedLast = JSON.parse(lastSaved);
          if (parsedLast && (parsedLast.isoDate || parsedLast.date)) {
            const isTodayLast =
              (parsedLast.isoDate && parsedLast.isoDate === todayIso) ||
              (parsedLast.date && parsedLast.date === todayDateStr);
            if (!isTodayLast) {
              localStorage.removeItem(lastKey);
            }
          }
        } catch (e) { }
      }

      const saved = localStorage.getItem(key);
      if (!saved) return {};
      const parsed = JSON.parse(saved);

      // Auto-archive and clean up if saved session is from a previous calendar day
      const sessionDate = parsed.sessionStartTime
        ? getIsoDateStr(parsed.sessionStartTime)
        : (parsed.lastEndedShift?.isoDate || (parsed.lastSavedTime ? getIsoDateStr(parsed.lastSavedTime) : null));

      if (sessionDate && sessionDate !== todayIso) {
        if (parsed.isShiftActive) {
          const autoShift = {
            id: `ATT-${(parsed.sessionStartTime || Date.now()).toString().slice(-4)}`,
            date: formatDateStr(parsed.sessionStartTime || Date.now()),
            isoDate: sessionDate,
            timestamp: parsed.sessionStartTime || Date.now(),
            loginTime: formatTimeStr(parsed.sessionStartTime || Date.now()),
            logoutTime: parsed.lastSavedTime ? formatTimeStr(parsed.lastSavedTime) : "--",
            activeSeconds: parsed.activeSeconds || 0,
            inactiveSeconds: parsed.inactiveSeconds || 0,
            breakSeconds: parsed.breakSeconds || 0,
            totalSeconds: parsed.sessionSeconds || 0,
            status: "Completed",
          };
          try {
            const attKey = getUserScopedStorageKey(STORAGE_ATTENDANCE_KEY, currentUsername);
            const prev = JSON.parse(localStorage.getItem(attKey)) || [];
            localStorage.setItem(attKey, JSON.stringify([autoShift, ...prev]));
          } catch (err) { }
        }
        localStorage.removeItem(key);
        return { isShiftActive: false, inactiveReason: "SHIFT_NOT_STARTED" };
      }

      // Check if there is an active break in progress
      if (parsed.currentBreak && parsed.currentBreak.startTime) {
        const elapsedSec = Math.max(0, Math.floor((Date.now() - parsed.currentBreak.startTime) / 1000));
        const expectedSec = (parsed.currentBreak.expectedDurationMinutes || 15) * 60;
        if (elapsedSec < expectedSec) {
          parsed.currentBreak.liveSeconds = elapsedSec;
          parsed.status = "ON_BREAK";
        } else {
          // Break duration has expired while page was closed/refreshing
          parsed.currentBreak = null;
          if (parsed.status === "ON_BREAK") {
            parsed.status = "ACTIVE";
          }
        }
      }

      return parsed;
    } catch (e) {
      return {};
    }
  };

  // Helper to retrieve today's completed shift metrics from user-scoped localStorage if shift ended today
  const getTodayCompletedMetrics = (customUsername = null) => {
    try {
      cleanupLegacyGlobalKeys();
      const todayDateStr = formatDateStr(Date.now());
      const todayIsoStr = getIsoDateStr(Date.now());

      const stateKey = getUserScopedStorageKey(STORAGE_STATE_KEY, customUsername);
      const lastKey = getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, customUsername);
      const attKey = getUserScopedStorageKey(STORAGE_ATTENDANCE_KEY, customUsername);

      // Check last ended shift record
      const savedLast = localStorage.getItem(lastKey);
      if (savedLast) {
        try {
          const parsed = JSON.parse(savedLast);
          if (parsed && (parsed.date === todayDateStr || parsed.isoDate === todayIsoStr) && parsed.status === "Completed") {
            // Phantom record check: do not treat locally synthesized "Recorded" logout as completed if no server record
            if (parsed.logoutTime === "Recorded" && !parsed.rawRecord) {
              return null;
            }
            return parsed;
          }
        } catch (e) { }
      }

      // Check cached attendance records
      const savedAtt = localStorage.getItem(attKey);
      if (savedAtt) {
        try {
          const list = JSON.parse(savedAtt);
          if (Array.isArray(list)) {
            const found = list.find((r) => {
              if (r.date !== todayDateStr && r.isoDate !== todayIsoStr) return false;
              const rawLogout = r.rawRecord?.desklogouttime || r.logoutTime;
              const hasLogout = rawLogout && rawLogout !== "0" && rawLogout !== "--" && String(rawLogout).trim() !== "0";
              return r.status === "Completed" || hasLogout;
            });
            if (found) return found;
          }
        } catch (e) { }
      }

      const saved = localStorage.getItem(stateKey);
      if (saved) {
        try {
          const parsedState = JSON.parse(saved);
          if (parsedState?.lastEndedShift) {
            const l = parsedState.lastEndedShift;
            if (l.date === todayDateStr || l.isoDate === todayIsoStr) return l;
          }
        } catch (e) { }
      }
    } catch (e) { }
    return null;
  };

  // Shift active tracking state: requires clicking 'Start Shift' to begin each morning shift
  // If user explicitly ended shift today, it MUST NOT auto-start!
  // If user did a normal logout without ending shift, it stays running in the background!
  const [isShiftActive, setIsShiftActive] = useState(() => {
    if (isShiftExplicitlyEndedToday()) {
      return false;
    }
    const saved = getSavedSession();
    const todayIso = getIsoDateStr(Date.now());
    if (saved.sessionStartTime && getIsoDateStr(saved.sessionStartTime) !== todayIso) {
      return false;
    }
    if (saved.isShiftActive === true && saved.inactiveReason !== "SHIFT_ENDED") {
      return true;
    }
    return false;
  });

  // Core tracking states
  const [status, setStatus] = useState(() => {
    if (isShiftExplicitlyEndedToday()) {
      return "INACTIVE";
    }
    const saved = getSavedSession();
    const todayIso = getIsoDateStr(Date.now());
    if (saved.sessionStartTime && getIsoDateStr(saved.sessionStartTime) !== todayIso) {
      return "INACTIVE";
    }
    if (saved.isShiftActive === true && saved.inactiveReason !== "SHIFT_ENDED") {
      if (saved.currentBreak) return "ON_BREAK";
      return "ACTIVE";
    }
    return "INACTIVE";
  }); // 'ACTIVE' | 'INACTIVE' | 'ON_BREAK'

  const [inactiveReason, setInactiveReason] = useState(() => {
    if (isShiftExplicitlyEndedToday()) {
      return "SHIFT_ENDED";
    }
    const saved = getSavedSession();
    const todayIso = getIsoDateStr(Date.now());
    if (saved.sessionStartTime && getIsoDateStr(saved.sessionStartTime) !== todayIso) {
      return "SHIFT_NOT_STARTED";
    }
    if (saved.isShiftActive === true && saved.inactiveReason !== "SHIFT_ENDED") {
      return null;
    }
    if (saved.inactiveReason === "SHIFT_ENDED") {
      const savedDate = saved.lastEndedShift?.isoDate || getIsoDateStr(saved.sessionStartTime);
      if (savedDate === todayIso) return "SHIFT_ENDED";
    }
    return saved.inactiveReason || "SHIFT_NOT_STARTED";
  });

  // Timing counters in seconds (retains today's completed work so it never resets to zero)
  const [activeSeconds, setActiveSeconds] = useState(() => {
    const completed = getTodayCompletedMetrics();
    if (completed) {
      return (
        parseScreenActiveTimeToSeconds(completed.screenactivetime) ||
        parseScreenActiveTimeToSeconds(completed.screenActiveTime) ||
        parseDurationToSeconds(completed.activeSeconds) ||
        Number(completed.activeSeconds) ||
        0
      );
    }
    const saved = getSavedSession();
    return (
      parseScreenActiveTimeToSeconds(saved.screenactivetime) ||
      parseScreenActiveTimeToSeconds(saved.screenActiveTime) ||
      parseDurationToSeconds(saved.activeSeconds) ||
      Number(saved.activeSeconds) ||
      0
    );
  });

  const [inactiveSeconds, setInactiveSeconds] = useState(() => {
    const completed = getTodayCompletedMetrics();
    if (completed) {
      return (
        parseDurationToSeconds(completed.desktotalidletime) ||
        Number(completed.idleSeconds) ||
        Number(completed.inactiveSeconds) ||
        0
      );
    }
    const saved = getSavedSession();
    return (
      parseDurationToSeconds(saved.desktotalidletime) ||
      parseDurationToSeconds(saved.idleTime) ||
      parseDurationToSeconds(saved.idletime) ||
      Number(saved.inactiveSeconds) ||
      Number(saved.idleSeconds) ||
      0
    );
  });

  const [breakSeconds, setBreakSeconds] = useState(() => {
    const completed = getTodayCompletedMetrics();
    if (completed) {
      return (
        parseBreakTimeToSeconds(completed.breaktime) ||
        parseBreakTimeToSeconds(completed.breakSeconds) ||
        0
      );
    }
    const saved = getSavedSession();
    return (
      parseBreakTimeToSeconds(saved.breaktime) ||
      parseBreakTimeToSeconds(saved.breakSeconds) ||
      0
    );
  });

  const [screenLockSeconds, setScreenLockSeconds] = useState(() => {
    const completed = getTodayCompletedMetrics();
    if (completed) {
      return (
        parseDurationToSeconds(completed.desktotalScreenlocktime) ||
        Number(completed.screenLockSeconds) ||
        0
      );
    }
    const saved = getSavedSession();
    return Number(saved.screenLockSeconds) || 0;
  });

  const [sessionSeconds, setSessionSeconds] = useState(() => {
    const completed = getTodayCompletedMetrics();
    if (completed) {
      const act =
        parseDurationToSeconds(completed.screenactivetime) ||
        Number(completed.activeSeconds) ||
        0;
      const inact =
        parseDurationToSeconds(completed.desktotalidletime) ||
        Number(completed.idleSeconds) ||
        Number(completed.inactiveSeconds) ||
        0;
      const lock =
        parseDurationToSeconds(completed.desktotalScreenlocktime) ||
        Number(completed.screenLockSeconds) ||
        0;
      const brk =
        parseBreakTimeToSeconds(completed.breaktime) ||
        parseBreakTimeToSeconds(completed.breakSeconds) ||
        0;
      return Math.max(
        Number(completed.totalSeconds) || 0,
        act + inact + lock + brk
      );
    }
    const saved = getSavedSession();
    if (saved.isShiftActive === true && saved.inactiveReason !== "SHIFT_ENDED" && saved.sessionStartTime) {
      const todayIso = getIsoDateStr(Date.now());
      if (getIsoDateStr(saved.sessionStartTime) === todayIso) {
        const elapsed = Math.max(0, Math.floor((Date.now() - saved.sessionStartTime) / 1000));
        return Math.max(Number(saved.sessionSeconds) || 0, elapsed);
      }
    }
    return Number(saved.sessionSeconds) || 0;
  });

  // Active break tracking
  const [currentBreak, setCurrentBreak] = useState(() => getSavedSession().currentBreak || null); // { id, type, startTime, liveSeconds }

  // Historical data
  const [attendanceHistory, setAttendanceHistory] = useState(() => getInitialAttendance());
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
  const [breakHistory, setBreakHistory] = useState(() => getInitialBreaks());
  const [activityLogs, setActivityLogs] = useState(() => getInitialLogs());

  const isFetchingAttendanceRef = useRef(false);

  // Fetch Attendance History from server API (/User/getdeskdetailsAgainstUserName)
  // and dynamically synchronize today's status & timers for the specific user!
  const fetchAttendanceHistory = async (customUsername = null) => {
    const username =
      customUsername ||
      sessionStorage.getItem("userName") ||
      employeeRef.current?.userName ||
      employeeRef.current?.username ||
      getCurrentLoggedInUsername();

    if (!username || username === "guest_user" || username === "guest") return;
    if (isFetchingAttendanceRef.current) return;
    isFetchingAttendanceRef.current = true;

    const todayIso = getIsoDateStr(Date.now());
    const userLastShiftKey = getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, username);
    const userStateKey = getUserScopedStorageKey(STORAGE_STATE_KEY, username);
    const userExplicitEndKey = getUserScopedStorageKey(STORAGE_EXPLICIT_END_KEY, username);

    setIsLoadingAttendance(true);
    try {
      const response = await getDeskDetailsAgainstUserName(username);
      console.log(`getdeskdetailsAgainstUserName response for ${username}:`, response?.data);
      let rawData = null;
      if (Array.isArray(response?.data)) {
        rawData = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        rawData = response.data.data;
      } else if (Array.isArray(response?.data?.list)) {
        rawData = response.data.list;
      } else if (Array.isArray(response?.data?.result)) {
        rawData = response.data.result;
      } else if (Array.isArray(response?.data?.deskDetails)) {
        rawData = response.data.deskDetails;
      } else if (Array.isArray(response?.data?.details)) {
        rawData = response.data.details;
      } else if (response?.data && typeof response?.data === "object") {
        if (response.data.data && typeof response.data.data === "object" && !Array.isArray(response.data.data)) {
          rawData = [response.data.data];
        } else if (
          response.data.screenactivetime !== undefined ||
          response.data.desklogintime !== undefined ||
          response.data.desktotalidletime !== undefined ||
          response.data.pkDeskId !== undefined ||
          response.data.deskusername !== undefined
        ) {
          rawData = [response.data];
        }
      }
      if (rawData !== null) {
        const transformed = rawData
          .map((item, idx) => transformServerDeskRecord(item, idx))
          .filter(Boolean)
          .map((record) => {
            if (record.isoDate === todayIso) {
              const savedLast = localStorage.getItem(userLastShiftKey);
              let lastShift = null;
              if (savedLast) {
                try {
                  lastShift = JSON.parse(savedLast);
                } catch (e) { }
              }
              const savedState = localStorage.getItem(userStateKey);
              let parsedState = null;
              if (savedState) {
                try {
                  parsedState = JSON.parse(savedState);
                } catch (e) { }
              }

              const hasServerLogout =
                record.rawRecord?.desklogouttime &&
                record.rawRecord.desklogouttime !== "0" &&
                String(record.rawRecord.desklogouttime).trim() !== "0";

              const isLastShiftToday =
                lastShift &&
                (lastShift.isoDate === todayIso || lastShift.date === formatDateStr(Date.now())) &&
                lastShift.status === "Completed";

              const isParsedStateEndedToday =
                parsedState &&
                !parsedState.isShiftActive &&
                parsedState.inactiveReason === "SHIFT_ENDED" &&
                (parsedState.lastEndedShift?.isoDate === todayIso || getIsoDateStr(parsedState.sessionStartTime) === todayIso);

              const isShiftEndedLocally =
                isShiftExplicitlyEndedToday(username) ||
                isParsedStateEndedToday ||
                isLastShiftToday;

              if (hasServerLogout || isShiftEndedLocally) {
                const realActive = parseScreenActiveTimeToSeconds(
                  record.rawRecord?.screenactivetime ?? record.screenactivetime ?? "0"
                );
                const realIdle =
                  parseDurationToSeconds(record.rawRecord?.desktotalidletime) ||
                  parseDurationToSeconds(record.rawRecord?.desktotal_idletime) ||
                  parseDurationToSeconds(record.rawRecord?.idleTime) ||
                  parseDurationToSeconds(record.rawRecord?.idletime) ||
                  parseDurationToSeconds(record.desktotalidletime) ||
                  Number(record.idleSeconds) ||
                  0;
                const realBreak = Math.max(
                  parseBreakTimeToSeconds(record.rawRecord?.breaktime) || 0,
                  parseBreakTimeToSeconds(record.breaktime) || 0,
                  Number(record.breakSeconds) || 0,
                  Number(lastShift?.breakSeconds) || 0,
                  Number(parsedState?.breakSeconds) || 0
                );
                const realScreenLock = Math.max(
                  parseDurationToSeconds(record.rawRecord?.desktotalScreenlocktime) || 0,
                  parseDurationToSeconds(record.desktotalScreenlocktime) || 0,
                  Number(record.screenLockSeconds) || 0,
                  Number(lastShift?.screenLockSeconds) || 0,
                  Number(screenLockSecondsRef.current) || 0
                );
                const realElapsed = record.timestamp ? Math.max(0, Math.floor((Date.now() - record.timestamp) / 1000)) : 0;
                const realTotal = Math.max(
                  realActive + realIdle + realScreenLock + realBreak,
                  realElapsed,
                  Number(record.totalSeconds) || 0,
                  Number(lastShift?.totalSeconds) || 0,
                  Number(parsedState?.sessionSeconds) || 0
                );
                const rawIdleTimeStr =
                  record.desktotalidletime !== undefined && record.desktotalidletime !== null && record.desktotalidletime !== ""
                    ? String(record.desktotalidletime)
                    : String(realIdle);
                const rawScreenLockStr =
                  record.desktotalScreenlocktime !== undefined && record.desktotalScreenlocktime !== null && record.desktotalScreenlocktime !== ""
                    ? String(record.desktotalScreenlocktime)
                    : String(realScreenLock);
                const rawBreakTimeStr =
                  record.breaktime !== undefined && record.breaktime !== null && record.breaktime !== ""
                    ? String(record.breaktime)
                    : String(realBreak);
                const rawActiveTimeStr =
                  record.screenactivetime !== undefined && record.screenactivetime !== null && record.screenactivetime !== ""
                    ? String(record.screenactivetime)
                    : String(realActive);

                return {
                  ...record,
                  status: "Completed",
                  screenActiveTime: realActive,
                  screenactivetime: rawActiveTimeStr,
                  activeSeconds: realActive,
                  inactiveSeconds: realIdle,
                  idleSeconds: realIdle,
                  idleTime: formatDuration(rawIdleTimeStr || realIdle),
                  desktotalidletime: rawIdleTimeStr,
                  screenLockSeconds: realScreenLock,
                  screenLockTime: formatDuration(rawScreenLockStr || realScreenLock),
                  desktotalScreenlocktime: rawScreenLockStr,
                  breakSeconds: realBreak,
                  breakTime: formatBreakTimeMinutes(rawBreakTimeStr || realBreak),
                  breaktime: rawBreakTimeStr,
                  totalSeconds: realTotal,
                  logoutTime: (record.logoutTime && record.logoutTime !== "--" && record.logoutTime !== "0") ? record.logoutTime : (lastShift?.logoutTime || "Recorded"),
                };
              }

              const serverIdle =
                parseDurationToSeconds(record.rawRecord?.desktotalidletime) ||
                parseDurationToSeconds(record.rawRecord?.desktotal_idletime) ||
                parseDurationToSeconds(record.rawRecord?.idleTime) ||
                parseDurationToSeconds(record.rawRecord?.idletime) ||
                parseDurationToSeconds(record.desktotalidletime) ||
                0;
              const serverBreak =
                parseBreakTimeToSeconds(record.rawRecord?.breaktime) ||
                parseBreakTimeToSeconds(record.breaktime) ||
                Number(record.breakSeconds) ||
                0;
              const serverScreenLock =
                parseDurationToSeconds(record.rawRecord?.desktotalScreenlocktime) ||
                parseDurationToSeconds(record.desktotalScreenlocktime) ||
                Number(record.screenLockSeconds) ||
                0;

              const effectiveActive = parseScreenActiveTimeToSeconds(
                record.rawRecord?.screenactivetime ?? record.screenactivetime ?? "0"
              );
              const effectiveIdle = serverIdle;
              const effectiveBreak = Math.max(serverBreak, Number(breakSecondsRef.current) || 0, Number(parsedState?.breakSeconds) || 0);
              const effectiveScreenLock = Math.max(serverScreenLock, Number(screenLockSecondsRef.current) || 0);
              const effectiveTotal = Math.max(
                effectiveActive + effectiveIdle + effectiveScreenLock + effectiveBreak,
                Number(sessionSecondsRef.current) || 0,
                Number(record.totalSeconds) || 0
              );
              const rawIdleTimeStr =
                record.desktotalidletime !== undefined && record.desktotalidletime !== null && record.desktotalidletime !== ""
                  ? String(record.desktotalidletime)
                  : String(effectiveIdle);
              const rawScreenLockStr =
                record.desktotalScreenlocktime !== undefined && record.desktotalScreenlocktime !== null && record.desktotalScreenlocktime !== ""
                  ? String(record.desktotalScreenlocktime)
                  : String(effectiveScreenLock);
              const rawBreakTimeStr =
                record.breaktime !== undefined && record.breaktime !== null && record.breaktime !== ""
                  ? String(record.breaktime)
                  : String(effectiveBreak);
              const rawActiveTimeStr =
                record.screenactivetime !== undefined && record.screenactivetime !== null && record.screenactivetime !== ""
                  ? String(record.screenactivetime)
                  : String(effectiveActive);

              return {
                ...record,
                status: "In Progress",
                screenActiveTime: effectiveActive,
                screenactivetime: rawActiveTimeStr,
                activeSeconds: effectiveActive,
                inactiveSeconds: effectiveIdle,
                idleSeconds: effectiveIdle,
                idleTime: formatDuration(rawIdleTimeStr || effectiveIdle),
                desktotalidletime: rawIdleTimeStr,
                screenLockSeconds: effectiveScreenLock,
                screenLockTime: formatDuration(rawScreenLockStr || effectiveScreenLock),
                desktotalScreenlocktime: rawScreenLockStr,
                breakSeconds: effectiveBreak,
                breakTime: formatBreakTimeMinutes(rawBreakTimeStr || effectiveBreak),
                breaktime: rawBreakTimeStr,
                totalSeconds: effectiveTotal,
                logoutTime: "--",
              };
            }
            return record;
          })
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

        setAttendanceHistory(transformed);

        // --- DIRECT API-BASED SHIFT STATUS DETERMINATION ---
        const todayRecords = transformed.filter(
          (r) => r.isoDate === todayIso || r.date === formatDateStr(Date.now())
        );

        // Find if any record today is actively in-progress on the server
        const activeRunningRecord = todayRecords.find((r) => {
          const rawLogout = r.rawRecord?.desklogouttime;
          return !rawLogout || rawLogout === "0" || String(rawLogout).trim() === "0" || String(rawLogout).trim() === "";
        });

        // Find if there is a record today where Shift End API was explicitly called
        const explicitEndedRecord = todayRecords.find((r) => {
          const rawLogout = r.rawRecord?.desklogouttime;
          return rawLogout && rawLogout !== "0" && String(rawLogout).trim() !== "0" && String(rawLogout).trim() !== "";
        });

        const endedRecord = explicitEndedRecord;

        const savedState = localStorage.getItem(userStateKey);
        let parsedState = null;
        if (savedState) {
          try {
            parsedState = JSON.parse(savedState);
          } catch (e) { }
        }

        const savedLast = localStorage.getItem(userLastShiftKey);
        let lastShift = null;
        if (savedLast) {
          try {
            lastShift = JSON.parse(savedLast);
            if (lastShift && (lastShift.isoDate || lastShift.date)) {
              const isToday =
                (lastShift.isoDate && lastShift.isoDate === todayIso) ||
                (lastShift.date && lastShift.date === formatDateStr(Date.now()));
              if (!isToday) {
                localStorage.removeItem(userLastShiftKey);
                lastShift = null;
              }
            }
          } catch (e) { }
        }

        const isLastShiftToday =
          lastShift &&
          (lastShift.isoDate === todayIso || lastShift.date === formatDateStr(Date.now())) &&
          lastShift.status === "Completed";

        const isParsedStateEndedToday =
          parsedState &&
          !parsedState.isShiftActive &&
          parsedState.inactiveReason === "SHIFT_ENDED" &&
          (parsedState.lastEndedShift?.isoDate === todayIso || getIsoDateStr(parsedState.sessionStartTime) === todayIso);

        const isShiftExplicitlyEndedLocally =
          isShiftExplicitlyEndedToday(username) ||
          isParsedStateEndedToday ||
          isLastShiftToday;

        // =========================================================================
        // PRIORITY 1: ACTIVE RUNNING SHIFT ON SERVER (desklogouttime is "0" or not set)
        // If server says employee is active, synchronize directly from API response!
        // =========================================================================
        if (activeRunningRecord) {
          localStorage.removeItem(userExplicitEndKey);

          let startTs = activeRunningRecord.timestamp;
          if (activeRunningRecord.rawRecord?.desklogintime) {
            const parsed = parseDateSafe(activeRunningRecord.rawRecord.desklogintime);
            if (parsed) startTs = parsed.getTime();
          }
          if (!startTs || isNaN(startTs)) {
            startTs = sessionStartTimeRef.current || parsedState?.sessionStartTime || Date.now();
          }

          const now = Date.now();
          const elapsedFromStart = Math.max(0, Math.floor((now - startTs) / 1000));

          // Real screen active time STRICTLY from response screenactivetime (minutes -> seconds)
          const serverActive = parseScreenActiveTimeToSeconds(
            activeRunningRecord.rawRecord?.screenactivetime ??
            activeRunningRecord.rawRecord?.screenActiveTime ??
            activeRunningRecord.rawRecord?.activetime ??
            activeRunningRecord.rawRecord?.activeTime ??
            activeRunningRecord.rawRecord?.desktotalscreenactivetime ??
            activeRunningRecord.rawRecord?.desktotalScreenactivetime ??
            activeRunningRecord.screenactivetime ??
            activeRunningRecord.screenActiveTime ??
            "0"
          );

          // Real idle time STRICTLY from response desktotalidletime
          const serverIdle = parseDurationToSeconds(
            activeRunningRecord.rawRecord?.desktotalidletime ??
            activeRunningRecord.rawRecord?.desktotal_idletime ??
            activeRunningRecord.rawRecord?.desktotalIdleTime ??
            activeRunningRecord.rawRecord?.deskTotalIdleTime ??
            activeRunningRecord.rawRecord?.idleTime ??
            activeRunningRecord.rawRecord?.idletime ??
            activeRunningRecord.rawRecord?.idle_time ??
            activeRunningRecord.rawRecord?.desktotalidle ??
            activeRunningRecord.desktotalidletime ??
            activeRunningRecord.idleSeconds ??
            "0"
          );

          // Real break time STRICTLY from response breaktime
          const serverBreak = parseBreakTimeToSeconds(
            activeRunningRecord.rawRecord?.breaktime ??
            activeRunningRecord.rawRecord?.breakTime ??
            activeRunningRecord.rawRecord?.break_time ??
            activeRunningRecord.breaktime ??
            activeRunningRecord.breakSeconds ??
            "0"
          );
          const localBreak = Number(breakSecondsRef.current) || 0;
          const finalBreak = Math.max(localBreak, serverBreak);

          // Real screen lock time STRICTLY from response desktotalScreenlocktime
          const serverScreenLock = parseDurationToSeconds(
            activeRunningRecord.rawRecord?.desktotalScreenlocktime ??
            activeRunningRecord.rawRecord?.desktotalscreenlocktime ??
            activeRunningRecord.rawRecord?.screenLockTime ??
            activeRunningRecord.desktotalScreenlocktime ??
            activeRunningRecord.screenLockSeconds ??
            "0"
          );
          const localScreenLock = Number(screenLockSecondsRef.current) || 0;
          const finalScreenLock = Math.max(localScreenLock, serverScreenLock);

          const finalActive = serverActive;
          const finalInactive = serverIdle;
          const finalSession = elapsedFromStart > 0
            ? elapsedFromStart
            : Math.max(0, Math.floor((now - startTs) / 1000));

          setIsShiftActive(true);
          isShiftActiveRef.current = true;
          setSystemTrackingEnabled(true);
          systemTrackingEnabledRef.current = true;

          // Preserve ON_BREAK state if user is currently taking a break
          const runningBreak = currentBreakRef.current || parsedState?.currentBreak;
          const isCurrentlyOnBreak =
            Boolean(runningBreak) ||
            statusRef.current === "ON_BREAK" ||
            parsedState?.status === "ON_BREAK";

          if (isCurrentlyOnBreak && runningBreak && runningBreak.startTime) {
            const elapsedSec = Math.max(0, Math.floor((Date.now() - runningBreak.startTime) / 1000));
            const expectedSec = (runningBreak.expectedDurationMinutes || 15) * 60;
            if (elapsedSec < expectedSec) {
              const updatedBreak = { ...runningBreak, liveSeconds: elapsedSec };
              setStatus("ON_BREAK");
              statusRef.current = "ON_BREAK";
              setCurrentBreak(updatedBreak);
              currentBreakRef.current = updatedBreak;
              setInactiveReason(null);
              inactiveReasonRef.current = null;
            } else {
              setStatus("ACTIVE");
              statusRef.current = "ACTIVE";
              setInactiveReason(null);
              inactiveReasonRef.current = null;
              setCurrentBreak(null);
              currentBreakRef.current = null;
            }
          } else {
            setStatus("ACTIVE");
            statusRef.current = "ACTIVE";
            setInactiveReason(null);
            inactiveReasonRef.current = null;
            setCurrentBreak(null);
            currentBreakRef.current = null;
          }

          setActiveSeconds(finalActive);
          activeSecondsRef.current = finalActive;
          setInactiveSeconds(finalInactive);
          inactiveSecondsRef.current = finalInactive;
          setBreakSeconds(finalBreak);
          breakSecondsRef.current = finalBreak;
          setScreenLockSeconds(finalScreenLock);
          screenLockSecondsRef.current = finalScreenLock;
          setSessionSeconds(finalSession);
          sessionSecondsRef.current = finalSession;
          setSessionStartTime(startTs);
          sessionStartTimeRef.current = startTs;
          lastTickRef.current = now;
          lastActivityRef.current = now;

          const snapshot = {
            isShiftActive: true,
            systemTrackingEnabled: true,
            sessionStartTime: startTs,
            activeSeconds: finalActive,
            inactiveSeconds: finalInactive,
            breakSeconds: finalBreak,
            screenLockSeconds: finalScreenLock,
            sessionSeconds: finalSession,
            screenactivetime: String(activeRunningRecord.rawRecord?.screenactivetime || activeRunningRecord.screenactivetime || finalActive),
            desktotalidletime: String(activeRunningRecord.rawRecord?.desktotalidletime || activeRunningRecord.desktotalidletime || finalInactive),
            breaktime: String(activeRunningRecord.rawRecord?.breaktime || activeRunningRecord.breaktime || finalBreak),
            desktotalScreenlocktime: String(activeRunningRecord.rawRecord?.desktotalScreenlocktime || activeRunningRecord.desktotalScreenlocktime || finalScreenLock),
            status: isCurrentlyOnBreak ? "ON_BREAK" : "ACTIVE",
            inactiveReason: null,
            currentBreak: isCurrentlyOnBreak ? (parsedState?.currentBreak || currentBreakRef.current) : null,
            lastSavedTime: now,
          };
          localStorage.setItem(userStateKey, JSON.stringify(snapshot));
          localStorage.removeItem(userLastShiftKey);
          return;
        }

        // =========================================================================
        // PRIORITY 2: SHIFT ENDED ON SERVER TODAY (desklogouttime !== "0")
        // =========================================================================
        if (endedRecord) {
          localStorage.setItem(userExplicitEndKey, todayIso);

          let startTs = endedRecord.timestamp;
          if (endedRecord.rawRecord?.desklogintime) {
            const parsed = parseDateSafe(endedRecord.rawRecord.desklogintime);
            if (parsed) startTs = parsed.getTime();
          }

          const finalWorked = parseScreenActiveTimeToSeconds(
            endedRecord.rawRecord?.screenactivetime ?? endedRecord.screenactivetime ?? "0"
          );
          const finalIdle = parseDurationToSeconds(
            endedRecord.rawRecord?.desktotalidletime ??
            endedRecord.rawRecord?.desktotal_idletime ??
            endedRecord.rawRecord?.idleTime ??
            endedRecord.rawRecord?.idletime ??
            endedRecord.desktotalidletime ??
            endedRecord.idleSeconds ??
            "0"
          );
          const finalBreak = parseBreakTimeToSeconds(
            endedRecord.rawRecord?.breaktime ?? endedRecord.breaktime ?? "0"
          );
          const finalScreenLock = parseDurationToSeconds(
            endedRecord.rawRecord?.desktotalScreenlocktime ?? endedRecord.desktotalScreenlocktime ?? "0"
          );
          const realElapsed = startTs ? Math.max(0, Math.floor((Date.now() - startTs) / 1000)) : 0;
          const finalTotal = Math.max(
            Number(endedRecord.totalSeconds) || 0,
            realElapsed,
            finalWorked + finalIdle + finalScreenLock + finalBreak
          );

          setIsShiftActive(false);
          isShiftActiveRef.current = false;
          setSystemTrackingEnabled(false);
          systemTrackingEnabledRef.current = false;
          setStatus("INACTIVE");
          statusRef.current = "INACTIVE";
          setInactiveReason("SHIFT_ENDED");
          inactiveReasonRef.current = "SHIFT_ENDED";
          setCurrentBreak(null);
          currentBreakRef.current = null;
          setActiveSeconds(finalWorked);
          activeSecondsRef.current = finalWorked;
          setInactiveSeconds(finalIdle);
          inactiveSecondsRef.current = finalIdle;
          setBreakSeconds(finalBreak);
          breakSecondsRef.current = finalBreak;
          setScreenLockSeconds(finalScreenLock);
          screenLockSecondsRef.current = finalScreenLock;
          setSessionSeconds(finalTotal);
          sessionSecondsRef.current = finalTotal;
          if (startTs) {
            setSessionStartTime(startTs);
            sessionStartTimeRef.current = startTs;
          }

          const shiftRecord = {
            id: endedRecord.id,
            date: endedRecord.date,
            isoDate: todayIso,
            timestamp: startTs || Date.now(),
            loginTime: endedRecord.loginTime,
            logoutTime: (endedRecord.logoutTime && endedRecord.logoutTime !== "--" && endedRecord.logoutTime !== "0") ? endedRecord.logoutTime : "Recorded",
            screenActiveTime: finalWorked,
            screenactivetime: String(endedRecord.rawRecord?.screenactivetime || endedRecord.screenactivetime || finalWorked),
            activeSeconds: finalWorked,
            inactiveSeconds: finalIdle,
            idleSeconds: finalIdle,
            idleTime: formatDuration(finalIdle),
            desktotalidletime: String(finalIdle),
            screenLockSeconds: finalScreenLock,
            screenLockTime: formatDuration(finalScreenLock),
            desktotalScreenlocktime: String(endedRecord.rawRecord?.desktotalScreenlocktime || endedRecord.desktotalScreenlocktime || finalScreenLock || "0"),
            breakSeconds: finalBreak,
            breakTime: formatBreakTimeMinutes(finalBreak),
            breaktime: String(finalBreak),
            totalSeconds: finalTotal,
            status: "Completed",
          };
          localStorage.setItem(userLastShiftKey, JSON.stringify(shiftRecord));
          localStorage.setItem(userStateKey, JSON.stringify({
            isShiftActive: false,
            systemTrackingEnabled: false,
            sessionStartTime: shiftRecord.timestamp,
            activeSeconds: finalWorked,
            inactiveSeconds: finalIdle,
            breakSeconds: finalBreak,
            screenLockSeconds: finalScreenLock,
            sessionSeconds: finalTotal,
            status: "INACTIVE",
            inactiveReason: "SHIFT_ENDED",
            currentBreak: null,
            lastEndedShift: shiftRecord,
            lastSavedTime: Date.now(),
          }));
          return;
        }

        // =========================================================================
        // PRIORITY 3: SHIFT EXPLICITLY ENDED LOCALLY TODAY
        // =========================================================================
        if (isShiftExplicitlyEndedLocally) {
          localStorage.setItem(userExplicitEndKey, todayIso);
          setIsShiftActive(false);
          isShiftActiveRef.current = false;
          setSystemTrackingEnabled(false);
          systemTrackingEnabledRef.current = false;
          setStatus("INACTIVE");
          statusRef.current = "INACTIVE";
          setInactiveReason("SHIFT_ENDED");
          inactiveReasonRef.current = "SHIFT_ENDED";
          return;
        }

        // =========================================================================
        // PRIORITY 4: NO RECORD TODAY & NOT ACTIVELY TRACKING -> SHIFT NOT STARTED
        // =========================================================================
        const isCurrentActiveFromToday =
          isShiftActiveRef.current &&
          sessionStartTimeRef.current &&
          getIsoDateStr(sessionStartTimeRef.current) === todayIso;

        if (todayRecords.length === 0 && !isCurrentActiveFromToday) {
          localStorage.removeItem(userExplicitEndKey);
          localStorage.removeItem(userLastShiftKey);
          localStorage.removeItem(userStateKey);

          setIsShiftActive(false);
          isShiftActiveRef.current = false;
          setSystemTrackingEnabled(false);
          systemTrackingEnabledRef.current = false;
          setStatus("INACTIVE");
          statusRef.current = "INACTIVE";
          setInactiveReason("SHIFT_NOT_STARTED");
          inactiveReasonRef.current = "SHIFT_NOT_STARTED";
          setActiveSeconds(0);
          activeSecondsRef.current = 0;
          setInactiveSeconds(0);
          inactiveSecondsRef.current = 0;
          setBreakSeconds(0);
          breakSecondsRef.current = 0;
          setScreenLockSeconds(0);
          screenLockSecondsRef.current = 0;
          setSessionSeconds(0);
          sessionSecondsRef.current = 0;
          setSessionStartTime(Date.now());
          sessionStartTimeRef.current = Date.now();
          setCurrentBreak(null);
          currentBreakRef.current = null;
          return;
        }

        // CHECK 3: If current window is actively tracking a shift without a new server record, verify date!
        if (isShiftActiveRef.current && inactiveReasonRef.current !== "SHIFT_ENDED") {
          const startTs = sessionStartTimeRef.current || Date.now();
          if (getIsoDateStr(startTs) !== todayIso) {
            // Previous day: reset state to Shift Not Started
            setIsShiftActive(false);
            isShiftActiveRef.current = false;
            setSystemTrackingEnabled(false);
            systemTrackingEnabledRef.current = false;
            setStatus("INACTIVE");
            statusRef.current = "INACTIVE";
            setInactiveReason("SHIFT_NOT_STARTED");
            inactiveReasonRef.current = "SHIFT_NOT_STARTED";
            setActiveSeconds(0);
            activeSecondsRef.current = 0;
            setInactiveSeconds(0);
            inactiveSecondsRef.current = 0;
            setBreakSeconds(0);
            breakSecondsRef.current = 0;
            setScreenLockSeconds(0);
            screenLockSecondsRef.current = 0;
            setSessionSeconds(0);
            sessionSecondsRef.current = 0;
            setSessionStartTime(Date.now());
            sessionStartTimeRef.current = Date.now();
            return;
          }
          return;
        }

        if (parsedState && parsedState.isShiftActive === true && parsedState.inactiveReason !== "SHIFT_ENDED") {
          // CASE 3: Local state is active and Shift End was not explicitly called
          const now = Date.now();
          const startTs = parsedState.sessionStartTime || sessionStartTimeRef.current || Date.now();
          if (getIsoDateStr(startTs) !== todayIso) {
            // Previous day: reset to fresh Shift Not Started
            localStorage.removeItem(userStateKey);
            setIsShiftActive(false);
            isShiftActiveRef.current = false;
            setSystemTrackingEnabled(false);
            systemTrackingEnabledRef.current = false;
            setStatus("INACTIVE");
            statusRef.current = "INACTIVE";
            setInactiveReason("SHIFT_NOT_STARTED");
            inactiveReasonRef.current = "SHIFT_NOT_STARTED";
            setActiveSeconds(0);
            activeSecondsRef.current = 0;
            setInactiveSeconds(0);
            inactiveSecondsRef.current = 0;
            setBreakSeconds(0);
            breakSecondsRef.current = 0;
            setScreenLockSeconds(0);
            screenLockSecondsRef.current = 0;
            setSessionSeconds(0);
            sessionSecondsRef.current = 0;
            setSessionStartTime(Date.now());
            sessionStartTimeRef.current = Date.now();
            return;
          }

          localStorage.removeItem(userExplicitEndKey);
          const elapsedFromStart = Math.max(0, Math.floor((now - startTs) / 1000));
          const finalActive = Number(parsedState.activeSeconds) || 0;
          const finalInactive = Number(parsedState.inactiveSeconds) || 0;
          const finalBreak = Number(parsedState.breakSeconds) || 0;
          const finalScreenLock = Number(parsedState.screenLockSeconds) || 0;
          const finalSession = Math.max(elapsedFromStart, finalActive + finalInactive + finalScreenLock + finalBreak);

          setIsShiftActive(true);
          isShiftActiveRef.current = true;
          setSystemTrackingEnabled(true);
          systemTrackingEnabledRef.current = true;
          setStatus(parsedState.currentBreak ? "ON_BREAK" : "ACTIVE");
          statusRef.current = parsedState.currentBreak ? "ON_BREAK" : "ACTIVE";
          setInactiveReason(null);
          inactiveReasonRef.current = null;
          setCurrentBreak(parsedState.currentBreak || null);
          currentBreakRef.current = parsedState.currentBreak || null;
          setActiveSeconds(finalActive);
          activeSecondsRef.current = finalActive;
          setInactiveSeconds(finalInactive);
          inactiveSecondsRef.current = finalInactive;
          setBreakSeconds(finalBreak);
          breakSecondsRef.current = finalBreak;
          setScreenLockSeconds(finalScreenLock);
          screenLockSecondsRef.current = finalScreenLock;
          setSessionSeconds(finalSession);
          sessionSecondsRef.current = finalSession;
          setSessionStartTime(startTs);
          sessionStartTimeRef.current = startTs;
          lastTickRef.current = now;
          lastActivityRef.current = now;
        } else {
          // CASE 4: Shift has not been started today yet
          setIsShiftActive(false);
          isShiftActiveRef.current = false;
          setStatus("INACTIVE");
          statusRef.current = "INACTIVE";
          setInactiveReason("SHIFT_NOT_STARTED");
          inactiveReasonRef.current = "SHIFT_NOT_STARTED";
          setActiveSeconds(0);
          activeSecondsRef.current = 0;
          setInactiveSeconds(0);
          inactiveSecondsRef.current = 0;
          setBreakSeconds(0);
          breakSecondsRef.current = 0;
          setScreenLockSeconds(0);
          screenLockSecondsRef.current = 0;
          setSessionSeconds(0);
          sessionSecondsRef.current = 0;
          setCurrentBreak(null);
          currentBreakRef.current = null;
        }
      } else {
        // No data available from server: clear attendance so no dummy or fallback data is displayed
        setAttendanceHistory([]);
      }
    } catch (err) {
      console.warn("Could not fetch attendance history from server:", err?.message || err);
      // No data available: clear attendance so no dummy or fallback data is displayed
      setAttendanceHistory([]);
    } finally {
      isFetchingAttendanceRef.current = false;
      setIsLoadingAttendance(false);
    }
  };

  // Auto-fetch attendance history from server on mount
  useEffect(() => {
    fetchAttendanceHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic auto-sync from server every 30s while shift is active
  // Keeps the dashboard cards (Active, Idle, Break) synchronized with desktop companion agent reports
  useEffect(() => {
    if (!isShiftActive) return;
    const interval = setInterval(() => {
      fetchAttendanceHistory();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShiftActive]);

  // Toast notifications for user feedback
  const [toasts, setToasts] = useState([]);

  // Session start time: Uses saved session start, today's completed shift time, or current time
  const [sessionStartTime, setSessionStartTime] = useState(() => {
    const saved = getSavedSession();
    if (saved.sessionStartTime) {
      return saved.sessionStartTime;
    }
    const completed = getTodayCompletedMetrics();
    if (completed?.timestamp) {
      return completed.timestamp;
    }
    return Date.now();
  });

  // References for reliable calculation without stale closures
  const isShiftActiveRef = useRef(isShiftActive);
  const activeDateRef = useRef(getIsoDateStr(Date.now()));
  const lastActivityRef = useRef(Date.now());
  const lastTickRef = useRef(Date.now());
  const statusRef = useRef(status);
  const inactiveReasonRef = useRef(inactiveReason);
  const currentBreakRef = useRef(currentBreak);
  const isTestModeRef = useRef(isTestMode);
  const activeSecondsRef = useRef(activeSeconds);
  const inactiveSecondsRef = useRef(inactiveSeconds);
  const breakSecondsRef = useRef(breakSeconds);
  const screenLockSecondsRef = useRef(screenLockSeconds);
  const sessionSecondsRef = useRef(sessionSeconds);
  const sessionStartTimeRef = useRef(sessionStartTime);

  // Keep refs in sync
  useEffect(() => {
    isShiftActiveRef.current = isShiftActive;
  }, [isShiftActive]);

  useEffect(() => {
    activeSecondsRef.current = activeSeconds;
  }, [activeSeconds]);

  useEffect(() => {
    inactiveSecondsRef.current = inactiveSeconds;
  }, [inactiveSeconds]);

  useEffect(() => {
    breakSecondsRef.current = breakSeconds;
  }, [breakSeconds]);

  useEffect(() => {
    screenLockSecondsRef.current = screenLockSeconds;
  }, [screenLockSeconds]);

  useEffect(() => {
    sessionSecondsRef.current = sessionSeconds;
  }, [sessionSeconds]);

  useEffect(() => {
    sessionStartTimeRef.current = sessionStartTime;
  }, [sessionStartTime]);

  // System-level tracking states (W3C IdleDetector / Desktop Agent)
  const [systemTrackingSupported, setSystemTrackingSupported] = useState(() => {
    return typeof window !== "undefined" && "IdleDetector" in window;
  });
  const [systemTrackingEnabled, setSystemTrackingEnabled] = useState(() => {
    if (isShiftExplicitlyEndedToday()) return false;
    const saved = getSavedSession();
    return saved.isShiftActive === true || saved.systemTrackingEnabled === true;
  });
  const systemTrackingEnabledRef = useRef(
    (() => {
      if (isShiftExplicitlyEndedToday()) return false;
      const saved = getSavedSession();
      return saved.isShiftActive === true || saved.systemTrackingEnabled === true;
    })()
  );

  useEffect(() => {
    systemTrackingEnabledRef.current = systemTrackingEnabled;
  }, [systemTrackingEnabled]);

  // Keep refs in sync
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    inactiveReasonRef.current = inactiveReason;
  }, [inactiveReason]);

  useEffect(() => {
    currentBreakRef.current = currentBreak;
  }, [currentBreak]);

  useEffect(() => {
    isTestModeRef.current = isTestMode;
  }, [isTestMode]);

  // Toast dispatcher helper
  const addToast = (type, message, description = "") => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast = { id, type, message, description, timestamp: Date.now() };
    setToasts((prev) => [newToast, ...prev.slice(0, 4)]); // Keep max 5 toasts

    // Auto dismiss after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  };

  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Activity Log dispatcher helper
  const addActivityLog = (type, title, details) => {
    const currentUsername = getCurrentLoggedInUsername();
    const now = Date.now();
    let badgeClass = "badge-event-default";
    if (type === "ACTIVE") badgeClass = "badge-event-active";
    if (type === "INACTIVE" || type === "IDLE") badgeClass = "badge-event-inactive";
    if (type === "BREAK_START") badgeClass = "badge-event-break-start";
    if (type === "BREAK_END") badgeClass = "badge-event-break-end";
    if (type === "LOGIN" || type === "SHIFT_START") badgeClass = "badge-event-login";
    if (type === "LOGOUT" || type === "SHIFT_END") badgeClass = "badge-event-logout";

    const entry = {
      id: `LOG-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      timestamp: now,
      isoDate: getIsoDateStr(now),
      dateStr: formatDateStr(now),
      timeStr: formatTimeStr(now),
      type,
      badgeClass,
      title,
      details,
    };

    setActivityLogs((prev) => {
      const updated = [entry, ...prev];
      localStorage.setItem(
        getUserScopedStorageKey(STORAGE_LOGS_KEY, currentUsername),
        JSON.stringify(updated.slice(0, 100))
      );
      return updated;
    });
  };

  // Ensure that whenever shift is active and current status is ACTIVE, the latest log shows ACTIVE
  useEffect(() => {
    if (isShiftActive && status === "ACTIVE") {
      const currentUsername = getCurrentLoggedInUsername();
      setActivityLogs((prev) => {
        if (!prev || prev.length === 0 || prev[0].type === "INACTIVE") {
          const entry = {
            id: `LOG-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            timestamp: Date.now(),
            isoDate: getIsoDateStr(Date.now()),
            dateStr: formatDateStr(Date.now()),
            timeStr: formatTimeStr(Date.now()),
            type: "ACTIVE",
            badgeClass: "badge-event-active",
            title: "Screen Active",
            details: "Laptop activity resumed across software.",
          };
          const updated = [entry, ...(prev || [])];
          localStorage.setItem(
            getUserScopedStorageKey(STORAGE_LOGS_KEY, currentUsername),
            JSON.stringify(updated.slice(0, 100))
          );
          return updated;
        }
        return prev;
      });
    }
  }, [isShiftActive, status]);

  // Reload session state when user switches account
  const reloadUserSession = (newUsername) => {
    cleanupLegacyGlobalKeys();
    const cleanUser = newUsername || getCurrentLoggedInUsername();
    const isEndedMarker = isShiftExplicitlyEndedToday(cleanUser);
    const saved = localStorage.getItem(getUserScopedStorageKey(STORAGE_STATE_KEY, cleanUser));
    let parsed = null;
    if (saved) {
      try {
        parsed = JSON.parse(saved);
      } catch (e) { }
    }

    const todayIso = getIsoDateStr(Date.now());
    const completed = getTodayCompletedMetrics(cleanUser);
    const parsedStateDate = parsed?.lastEndedShift?.isoDate || getIsoDateStr(parsed?.sessionStartTime);
    const isParsedEndedToday = parsed && parsed.inactiveReason === "SHIFT_ENDED" && !parsed?.isShiftActive && parsedStateDate === todayIso;
    const isExplicitlyEnded = isEndedMarker || isParsedEndedToday;

    if (isExplicitlyEnded) {
      const activeVal =
        parseDurationToSeconds(completed?.screenactivetime) ||
        parseDurationToSeconds(completed?.screenActiveTime) ||
        parseDurationToSeconds(completed?.activeSeconds) ||
        Number(completed?.activeSeconds) ||
        0;
      const inactiveVal = Math.max(Number(completed?.inactiveSeconds) || 0, Number(parsed?.inactiveSeconds) || 0);
      const breakVal = Math.max(Number(completed?.breakSeconds) || 0, Number(parsed?.breakSeconds) || 0);
      const sessionVal = Math.max(
        Number(completed?.totalSeconds) || 0,
        Number(parsed?.sessionSeconds) || 0,
        activeVal + inactiveVal + breakVal
      );

      setActiveSeconds(activeVal);
      activeSecondsRef.current = activeVal;
      setInactiveSeconds(inactiveVal);
      inactiveSecondsRef.current = inactiveVal;
      setBreakSeconds(breakVal);
      breakSecondsRef.current = breakVal;
      setSessionSeconds(sessionVal);
      sessionSecondsRef.current = sessionVal;

      const startTs = completed?.timestamp || parsed?.sessionStartTime;
      if (startTs) {
        setSessionStartTime(startTs);
        sessionStartTimeRef.current = startTs;
      }

      setIsShiftActive(false);
      isShiftActiveRef.current = false;
      setSystemTrackingEnabled(false);
      systemTrackingEnabledRef.current = false;
      setStatus("INACTIVE");
      statusRef.current = "INACTIVE";
      setInactiveReason("SHIFT_ENDED");
      inactiveReasonRef.current = "SHIFT_ENDED";
      setCurrentBreak(null);
      currentBreakRef.current = null;
    } else if (parsed && parsed.isShiftActive === true && parsed.inactiveReason !== "SHIFT_ENDED") {
      const now = Date.now();
      const startTs = parsed.sessionStartTime || Date.now();
      const elapsedFromStart = Math.max(0, Math.floor((now - startTs) / 1000));
      const activeVal =
        parseDurationToSeconds(parsed.screenactivetime) ||
        parseDurationToSeconds(parsed.screenActiveTime) ||
        parseDurationToSeconds(parsed.activeSeconds) ||
        Number(parsed.activeSeconds) ||
        0;
      const breakVal = Number(parsed.breakSeconds) || 0;
      const lockVal = Number(parsed.screenLockSeconds) || 0;
      const inactiveVal =
        parseDurationToSeconds(parsed.desktotalidletime) ||
        parseDurationToSeconds(parsed.idleTime) ||
        parseDurationToSeconds(parsed.idletime) ||
        Number(parsed.inactiveSeconds) ||
        Number(parsed.idleSeconds) ||
        0;
      const sessionVal = Math.max(Number(parsed.sessionSeconds) || 0, elapsedFromStart, activeVal + inactiveVal + breakVal + lockVal);

      setActiveSeconds(activeVal);
      activeSecondsRef.current = activeVal;
      setInactiveSeconds(inactiveVal);
      inactiveSecondsRef.current = inactiveVal;
      setBreakSeconds(breakVal);
      breakSecondsRef.current = breakVal;
      setSessionSeconds(sessionVal);
      sessionSecondsRef.current = sessionVal;

      setSessionStartTime(startTs);
      sessionStartTimeRef.current = startTs;

      setIsShiftActive(true);
      isShiftActiveRef.current = true;
      setStatus(parsed.currentBreak ? "ON_BREAK" : "ACTIVE");
      statusRef.current = parsed.currentBreak ? "ON_BREAK" : "ACTIVE";
      setInactiveReason(null);
      inactiveReasonRef.current = null;
      setCurrentBreak(parsed.currentBreak || null);
      currentBreakRef.current = parsed.currentBreak || null;
      if (parsed.systemTrackingEnabled !== false) {
        setSystemTrackingEnabled(true);
        systemTrackingEnabledRef.current = true;
      }
      lastTickRef.current = now;
      lastActivityRef.current = now;
    } else {
      setIsShiftActive(false);
      isShiftActiveRef.current = false;
      setStatus("INACTIVE");
      statusRef.current = "INACTIVE";
      setInactiveReason("SHIFT_NOT_STARTED");
      inactiveReasonRef.current = "SHIFT_NOT_STARTED";
      setActiveSeconds(0);
      activeSecondsRef.current = 0;
      setInactiveSeconds(0);
      inactiveSecondsRef.current = 0;
      setBreakSeconds(0);
      breakSecondsRef.current = 0;
      setSessionSeconds(0);
      sessionSecondsRef.current = 0;
      setCurrentBreak(null);
      currentBreakRef.current = null;
    }

    setAttendanceHistory(getInitialAttendance(cleanUser));
    setBreakHistory(getInitialBreaks(cleanUser));
    setActivityLogs(getInitialLogs(cleanUser));
  };

  // Rehydrate saved session state on mount
  useEffect(() => {
    reloadUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect user login / logout / switch in sessionStorage dynamically without high-frequency polling
  useEffect(() => {
    const checkUserChange = () => {
      const currentUserName = sessionStorage.getItem("userName");
      const currentPkUserId = sessionStorage.getItem("pkUserId");
      if (!currentUserName) return;

      const normCurrent = String(currentUserName).trim().toLowerCase();
      const normEmp = String(employeeRef.current?.userName || "").trim().toLowerCase();

      if (normCurrent !== normEmp) {
        const firstName = sessionStorage.getItem("firstName") || "";
        const lastName = sessionStorage.getItem("lastName") || "";
        const role = sessionStorage.getItem("designation") || sessionStorage.getItem("role") || "Employee";
        const name = [firstName, lastName].filter(Boolean).join(" ") || currentUserName || "Employee";
        const updatedEmp = {
          id: currentPkUserId || employeeRef.current?.id || currentUserName || "EMP-001",
          name,
          firstName: firstName || name,
          lastName: lastName || "",
          role,
          userName: currentUserName,
          username: currentUserName,
        };
        employeeRef.current = updatedEmp;
        setEmployee(updatedEmp);
        reloadUserSession(currentUserName);
        fetchAttendanceHistory(currentUserName);
      }
    };

    // Check once on mount
    checkUserChange();

    // Listen to storage events across tabs (e.g. login / logout in another tab)
    const handleStorageChange = (e) => {
      if (!e.key || e.key === "userName" || e.key === "pkUserId") {
        checkUserChange();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic and beforeunload state persistence to localStorage (user-scoped)
  useEffect(() => {
    const saveState = () => {
      const currentUsername =
        (employeeRef.current?.userName && employeeRef.current.userName !== "guest_user" && employeeRef.current.userName.trim()) ||
        (employeeRef.current?.username && employeeRef.current.username !== "guest_user" && employeeRef.current.username.trim()) ||
        sessionStorage.getItem("userName") ||
        localStorage.getItem("userName") ||
        getCurrentLoggedInUsername();
      const isEnded = isShiftExplicitlyEndedToday(currentUsername) && !isShiftActiveRef.current;
      const effShiftActive = isShiftActiveRef.current;
      const effInactiveReason = isEnded ? "SHIFT_ENDED" : inactiveReasonRef.current;
      const effStatus = isEnded ? "INACTIVE" : statusRef.current;

      const stateSnapshot = {
        isShiftActive: effShiftActive,
        systemTrackingEnabled: isEnded ? false : systemTrackingEnabledRef.current,
        sessionStartTime: sessionStartTimeRef.current,
        activeSeconds: activeSecondsRef.current,
        inactiveSeconds: inactiveSecondsRef.current,
        breakSeconds: breakSecondsRef.current,
        screenLockSeconds: screenLockSecondsRef.current,
        sessionSeconds: sessionSecondsRef.current,
        screenactivetime: String(activeSecondsRef.current || 0),
        desktotalidletime: String(inactiveSecondsRef.current || 0),
        breaktime: String(breakSecondsRef.current || 0),
        desktotalScreenlocktime: String(screenLockSecondsRef.current || 0),
        status: effStatus,
        inactiveReason: effInactiveReason,
        currentBreak: isEnded ? null : currentBreakRef.current,
        lastSavedTime: Date.now(),
        lastEndedShift: effShiftActive ? null : getTodayCompletedMetrics(currentUsername),
      };
      localStorage.setItem(
        getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername),
        JSON.stringify(stateSnapshot)
      );
    };

    // Save periodically every 5 seconds without churning on every tick
    const interval = setInterval(saveState, 5000);
    window.addEventListener("beforeunload", saveState);

    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", saveState);
      saveState();
    };
  }, [isShiftActive]);

  // Synchronously update both ref and state so callbacks and timers always have instant status
  const setLiveStatus = (newStatus, reason = null) => {
    statusRef.current = newStatus;
    inactiveReasonRef.current = reason;
    setStatus(newStatus);
    setInactiveReason(reason);
  };

  // Immediate resume function called whenever user unlocks screen, interacts, or focuses
  const resumeActive = (forceLog = false) => {
    if (!isShiftActiveRef.current) return;
    if (inactiveReasonRef.current === "SHIFT_ENDED") return;
    if (statusRef.current === "ON_BREAK") return;
    const wasInactive = statusRef.current === "INACTIVE";
    lastActivityRef.current = Date.now();
    lastTickRef.current = Date.now();
    if (wasInactive || forceLog || statusRef.current !== "ACTIVE") {
      setLiveStatus("ACTIVE", null);
      addToast("success", "Screen Active", "Laptop activity resumed.");
      addActivityLog("ACTIVE", "Screen Active", "Laptop activity resumed.");
    }
  };

  // Activity detection: listen to mouse and keyboard events on this window
  useEffect(() => {
    const handleUserActivity = () => {
      if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") return;
      if (statusRef.current === "INACTIVE") {
        resumeActive(true);
      } else {
        lastActivityRef.current = Date.now();
      }
    };

    // Throttled event listener to prevent CPU overhead
    let throttleTimeout = null;
    const throttledListener = () => {
      if (!throttleTimeout) {
        handleUserActivity();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, 800);
      }
    };

    const handleFocus = () => {
      if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") return;
      resumeActive();
    };

    const events = ["mousemove", "mousedown", "keydown", "keyup", "scroll", "touchstart", "click", "pointerdown", "pointermove", "focus"];
    events.forEach((ev) => window.addEventListener(ev, throttledListener, { passive: true }));
    window.addEventListener("focus", handleFocus);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, throttledListener));
      window.removeEventListener("focus", handleFocus);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // System IdleDetector (W3C Standard supported in Chrome & Edge for ANY software)
  const requestSystemIdleTracking = async () => {
    if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") {
      return;
    }
    setSystemTrackingEnabled(true);
    systemTrackingEnabledRef.current = true;
    if (typeof window === "undefined" || !("IdleDetector" in window)) {
      return;
    }
    try {
      const state = await window.IdleDetector.requestPermission();
      if (state === "granted") {
        const detector = new window.IdleDetector();
        detector.addEventListener("change", () => {
          if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") return;
          const { userState, screenState } = detector;
          // userState: 'active' | 'idle' across all laptop apps
          // screenState: 'locked' | 'unlocked'
          if (screenState === "locked") {
            if (statusRef.current !== "ON_BREAK") {
              setLiveStatus("INACTIVE", "LOCKED");
              addActivityLog("INACTIVE", "Screen Inactive", "Laptop screen locked (Windows/Mac lock).");
            }
          } else if (screenState === "unlocked") {
            // User just unlocked their laptop screen! Immediately resume active!
            resumeActive(true);
          } else if (userState === "active") {
            resumeActive(true);
          }
        });
        const thresholdMs = Math.max(60000, isTestModeRef.current ? 60000 : 900000);
        await detector.start({ threshold: thresholdMs });
        setSystemTrackingEnabled(true);
        systemTrackingEnabledRef.current = true;
      }
    } catch (err) {
      console.warn("IdleDetector error:", err);
    }
  };

  // Check if IdleDetector is available on mount & auto-activate if already permitted or shift is active
  useEffect(() => {
    if (typeof window !== "undefined" && "IdleDetector" in window) {
      setSystemTrackingSupported(true);
      const saved = getSavedSession();
      const completed = getTodayCompletedMetrics();
      // CRITICAL: If shift ended today, do not auto-activate idle detection!
      if (!completed && saved.inactiveReason !== "SHIFT_ENDED" && (saved.isShiftActive || saved.systemTrackingEnabled)) {
        requestSystemIdleTracking().catch(() => { });
      } else if (!completed && saved.inactiveReason !== "SHIFT_ENDED" && navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: "idle-detection" }).then((permissionStatus) => {
          if (permissionStatus.state === "granted") {
            requestSystemIdleTracking().catch(() => { });
          }
        }).catch(() => { });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Visibility and focus listener: Keep active when switching across software or unlocking
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") return;
      if (document.visibilityState === "visible") {
        resumeActive();
      } else {
        // Tab went to background: User switched to another desktop software (VS Code, Excel, etc.)
        lastActivityRef.current = Date.now();
      }
    };

    const handleFocus = () => {
      if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") return;
      resumeActive();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Main 1-second timer tick: Accurately tracks active work across ALL software without false inactives
  useEffect(() => {
    lastTickRef.current = Date.now();

    const onTick = () => {
      const now = Date.now();
      const currentIsoDate = getIsoDateStr(now);

      // Midnight Rollover (12:00 AM): Automatically roll over to a fresh shift state for the new calendar date
      if (activeDateRef.current && activeDateRef.current !== currentIsoDate) {
        activeDateRef.current = currentIsoDate;
        const currentUsername = getCurrentLoggedInUsername();
        localStorage.removeItem(getUserScopedStorageKey(STORAGE_EXPLICIT_END_KEY, currentUsername));
        localStorage.removeItem(getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, currentUsername));
        localStorage.removeItem(getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername));

        setIsShiftActive(false);
        isShiftActiveRef.current = false;
        setSystemTrackingEnabled(false);
        systemTrackingEnabledRef.current = false;
        setStatus("INACTIVE");
        statusRef.current = "INACTIVE";
        setInactiveReason("SHIFT_NOT_STARTED");
        inactiveReasonRef.current = "SHIFT_NOT_STARTED";
        setActiveSeconds(0);
        activeSecondsRef.current = 0;
        setInactiveSeconds(0);
        inactiveSecondsRef.current = 0;
        setBreakSeconds(0);
        breakSecondsRef.current = 0;
        setScreenLockSeconds(0);
        screenLockSecondsRef.current = 0;
        setSessionSeconds(0);
        sessionSecondsRef.current = 0;
        setSessionStartTime(now);
        sessionStartTimeRef.current = now;
        setCurrentBreak(null);
        currentBreakRef.current = null;
        lastTickRef.current = now;
        lastActivityRef.current = now;

        if (typeof fetchAttendanceHistory === "function") {
          fetchAttendanceHistory(currentUsername).catch(() => { });
        }
        return;
      }

      // If morning shift is not active or shift ended today, do not accumulate shift counters
      if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") {
        lastTickRef.current = now;
        lastActivityRef.current = now;
        return;
      }

      const onBreak = statusRef.current === "ON_BREAK";
      const idleThreshold = isTestModeRef.current ? 30 * 1000 : 15 * 60 * 1000;
      const isTabVisible = typeof document !== "undefined" ? document.visibilityState === "visible" : true;
      const hasFocus = typeof document !== "undefined" ? document.hasFocus() : true;

      // Elapsed seconds since last tick
      const deltaSec = Math.max(1, Math.floor((now - lastTickRef.current) / 1000));
      lastTickRef.current = now;

      // Real laptop sleep / screen lock detection:
      // ONLY if timer was frozen for >= 120 seconds (at least 2 full minutes, meaning laptop went to sleep or lid closed)
      if (deltaSec >= 120 && !onBreak) {
        const sleepSec = deltaSec;
        setLiveStatus("INACTIVE", "LOCKED");
        // Reset lastActivityRef so that when laptop wakes up, idle timer starts fresh from now
        lastActivityRef.current = now;
        addToast("warning", "Screen Inactive", `Laptop screen was asleep or locked for ${formatDurationHuman(sleepSec)}.`);
        addActivityLog("INACTIVE", "Screen Inactive", `Laptop screen was asleep or locked for ${formatDurationHuman(sleepSec)}.`);
        return;
      }

      // If user is working in another desktop application (tab hidden or window not focused):
      // User is actively working across software on their PC!
      // Keep lastActivityRef refreshed so in-browser idle timer NEVER marks them inactive!
      if (!onBreak && (!isTabVisible || !hasFocus)) {
        lastActivityRef.current = now;
      }

      const idleTime = now - lastActivityRef.current;

      // 1. Break handling (Work counter is stopped; break countdown counts down to auto-finish)
      if (onBreak) {
        const expectedMin = currentBreakRef.current?.expectedDurationMinutes || 15;
        const expectedDurationSec = isTestModeRef.current ? 30 : expectedMin * 60;

        setBreakSeconds((prev) => {
          const base = Math.max(prev, breakSecondsRef.current || 0);
          const next = base + deltaSec;
          breakSecondsRef.current = next;
          return next;
        });

        const prevLive = currentBreakRef.current?.liveSeconds || 0;
        const nextLive = prevLive + deltaSec;

        // Auto-finish break when scheduled duration has elapsed
        if (nextLive >= expectedDurationSec) {
          const b = currentBreakRef.current;
          const finalDurationSec = nextLive;
          const finalDurationHuman = formatDurationHuman(finalDurationSec);

          const completedBreak = {
            id: b?.id || `BRK-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`,
            type: b?.type || "Break",
            reason: b?.reason || b?.type || "Break",
            date: b?.date || formatDateStr(Date.now()),
            isoDate: b?.isoDate || getIsoDateStr(Date.now()),
            timestamp: b?.startTime || Date.now(),
            startTime: b?.startTimeStr || formatTimeStr(b?.startTime || Date.now()),
            endTime: formatTimeStr(Date.now()),
            durationSeconds: finalDurationSec,
            durationFormatted: finalDurationHuman,
            status: "Completed",
          };

          setBreakHistory((prev) => {
            const filtered = (prev || []).filter((item) => item.id !== completedBreak.id);
            const updated = [completedBreak, ...filtered];
            localStorage.setItem(getUserScopedStorageKey(STORAGE_BREAKS_KEY), JSON.stringify(updated));
            return updated;
          });

          setCurrentBreak(null);
          currentBreakRef.current = null;
          lastActivityRef.current = Date.now();
          setLiveStatus("ACTIVE", null);

          try {
            const currentUsername = getCurrentLoggedInUsername();
            const savedRaw = localStorage.getItem(getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername));
            const existingState = savedRaw ? JSON.parse(savedRaw) : {};
            localStorage.setItem(
              getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername),
              JSON.stringify({
                ...existingState,
                status: "ACTIVE",
                inactiveReason: null,
                currentBreak: null,
                lastSavedTime: Date.now(),
              })
            );
          } catch (e) { }

          addToast(
            "info",
            `${b?.type || "Break"} Finished Automatically`,
            `Scheduled ${expectedMin} min break ended. Shift countdown and active tracking resumed.`
          );
          addActivityLog(
            "BREAK_END",
            `${b?.type || "Break"} Auto-Finished`,
            `Break time (${expectedMin} mins) completed automatically. Work counter resumed.`
          );
          addActivityLog("ACTIVE", "Screen Active", "Break ended automatically - resumed active shift tracking.");
        } else {
          setCurrentBreak((prev) => (prev ? { ...prev, liveSeconds: nextLive } : null));
          if (currentBreakRef.current) {
            currentBreakRef.current.liveSeconds = nextLive;
          }
        }
      }
      // 2. Idle timeout handling (ONLY when Petropulse window is focused in foreground and untouched for idleThreshold)
      else if (isTabVisible && hasFocus && idleTime >= idleThreshold) {
        if (statusRef.current !== "INACTIVE") {
          setLiveStatus("INACTIVE", "INACTIVE");
          const modeMsg = isTestModeRef.current ? "30s" : "15 minutes";
          addToast("warning", "Screen Inactive", `No activity on screen for ${modeMsg}.`);
          addActivityLog("INACTIVE", "Screen Inactive", `No activity detected on screen for ${modeMsg}.`);
        }
        // Inactive / Idle Time is strictly synchronized from server response desktotalidletime
      }
      // 3. Normal active work (Active Time increments live tick-by-tick while shift is active and synchronized with server screenactivetime)
      else {
        if (!isShiftActiveRef.current || inactiveReasonRef.current === "SHIFT_ENDED") return;
        if (statusRef.current !== "ACTIVE") {
          resumeActive(true);
        }
        setActiveSeconds((prev) => {
          const next = (prev || 0) + deltaSec;
          activeSecondsRef.current = next;
          return next;
        });
      }

      // Always calculate total session duration strictly from login time
      const liveSessionSec = sessionStartTimeRef.current
        ? Math.max(0, Math.floor((now - sessionStartTimeRef.current) / 1000))
        : (sessionSecondsRef.current || 0) + deltaSec;
      sessionSecondsRef.current = liveSessionSec;
      setSessionSeconds(liveSessionSec);
    };

    // Use an inline Web Worker so Chrome never throttles the 1-second interval in background tabs
    let worker = null;
    let fallbackInterval = null;

    try {
      const blob = new Blob([
        `let timer = null;
         self.onmessage = function(e) {
           if (e.data === 'start') {
             if (timer) clearInterval(timer);
             timer = setInterval(function() { self.postMessage('tick'); }, 1000);
           } else if (e.data === 'stop') {
             if (timer) clearInterval(timer);
             timer = null;
           }
         };`
      ], { type: "application/javascript" });
      const workerUrl = URL.createObjectURL(blob);
      worker = new Worker(workerUrl);
      worker.onmessage = () => onTick();
      worker.postMessage("start");
    } catch (e) {
      // Fallback if Web Workers are restricted in certain environments
      fallbackInterval = setInterval(onTick, 1000);
    }

    return () => {
      if (worker) {
        try {
          worker.postMessage("stop");
          worker.terminate();
        } catch (e) { }
      }
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Break Management Functions
  const startBreak = async (breakType, reason = "", durationMinutes = null) => {
    if (currentBreakRef.current) {
      addToast("warning", "Break in progress", "Please end your current break before starting a new one.");
      return { success: false, message: "Break already in progress" };
    }

    if (!isShiftActiveRef.current) {
      addToast("warning", "Shift Not Active", "You must start your shift before taking a break.");
      return { success: false, message: "Shift not active" };
    }

    // Determine break duration in minutes (15 for Tea, 30 for Lunch, or chosen dropdown value)
    let breakTimeMinutes = 15;
    if (durationMinutes !== null && durationMinutes !== undefined) {
      const parsed = parseInt(String(durationMinutes).replace(/\D/g, ""), 10);
      if (!isNaN(parsed) && parsed > 0) {
        breakTimeMinutes = parsed;
      }
    } else {
      const typeLower = String(breakType || "").toLowerCase();
      if (typeLower.includes("lunch")) {
        breakTimeMinutes = 30;
      } else if (typeLower.includes("tea")) {
        breakTimeMinutes = 15;
      } else {
        const match = String(reason || "").match(/(\d+)\s*min/i);
        if (match) {
          breakTimeMinutes = parseInt(match[1], 10);
        } else {
          breakTimeMinutes = 15;
        }
      }
    }

    const currentUsername = getCurrentLoggedInUsername();

    // Call server API FIRST: /User/updateBreakTime?username=...&breakTime=...
    // ONLY execute break if the API call succeeds; otherwise FAIL and abort break!
    try {
      console.log(`[startBreak] Calling /User/updateBreakTime for "${currentUsername}" with breakTime: ${breakTimeMinutes}`);
      const apiRes = await updateBreakTime(currentUsername, breakTimeMinutes);
      console.log("[startBreak] updateBreakTime API response:", apiRes);

      const isHttpSuccess = apiRes && apiRes.status >= 200 && apiRes.status < 300;
      if (!isHttpSuccess) {
        throw new Error(`Server returned status ${apiRes?.status || "unknown error"}`);
      }
    } catch (apiErr) {
      console.error("[startBreak] updateBreakTime API failed:", apiErr);
      const errMsg =
        apiErr?.response?.data?.message ||
        apiErr?.response?.data?.error ||
        apiErr?.message ||
        "Failed to update break time on server.";

      addToast(
        "danger",
        "Failed to Start Break",
        `Server update failed: ${errMsg}. Break not started.`
      );
      addActivityLog(
        "ERROR",
        "Break Request Failed",
        `Server rejected updateBreakTime (${breakTimeMinutes} mins): ${errMsg}. Break was not executed.`
      );

      // Do NOT execute break! Return failure immediately!
      return { success: false, error: apiErr, message: errMsg };
    }

    // ONLY EXECUTED IF API CALL SUCCEEDED:
    const breakId = `BRK-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`;
    const reasonText = (reason && reason.trim()) || breakType;
    const newBreak = {
      id: breakId,
      type: breakType,
      reason: reasonText,
      expectedDurationMinutes: breakTimeMinutes,
      startTime: Date.now(),
      startTimeStr: formatTimeStr(Date.now()),
      liveSeconds: 0,
      date: formatDateStr(Date.now()),
    };

    setCurrentBreak(newBreak);
    currentBreakRef.current = newBreak;
    setStatus("ON_BREAK");
    statusRef.current = "ON_BREAK";
    setInactiveReason(null);
    inactiveReasonRef.current = null;

    // Immediately persist to localStorage so a page refresh preserves the break
    try {
      const stateSnapshot = {
        isShiftActive: true,
        systemTrackingEnabled: true,
        sessionStartTime: sessionStartTimeRef.current || Date.now(),
        activeSeconds: activeSecondsRef.current || 0,
        inactiveSeconds: inactiveSecondsRef.current || 0,
        breakSeconds: breakSecondsRef.current || 0,
        screenLockSeconds: screenLockSecondsRef.current || 0,
        sessionSeconds: sessionSecondsRef.current || 0,
        status: "ON_BREAK",
        inactiveReason: null,
        currentBreak: newBreak,
        lastSavedTime: Date.now(),
        lastEndedShift: null,
      };
      localStorage.setItem(getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername), JSON.stringify(stateSnapshot));
    } catch (e) { }

    addToast("success", `${breakType} Started (${breakTimeMinutes} mins)`, `Break time updated on server. Reason: ${reasonText}`);
    addActivityLog("BREAK_START", `${breakType} Started`, `Employee initiated a ${breakType} (~${breakTimeMinutes} mins). Server update confirmed. Reason: ${reasonText}`);

    return { success: true, break: newBreak };
  };

  const endBreak = () => {
    if (!currentBreakRef.current) return;

    const b = currentBreakRef.current;
    const durationSec = Math.max(1, Math.floor((Date.now() - b.startTime) / 1000));
    const durationHuman = formatDurationHuman(durationSec);

    const completedBreak = {
      id: b.id,
      type: b.type,
      reason: b.reason || b.type,
      date: b.date || formatDateStr(Date.now()),
      isoDate: b.isoDate || getIsoDateStr(Date.now()),
      timestamp: b.startTime,
      startTime: b.startTimeStr || formatTimeStr(b.startTime),
      endTime: formatTimeStr(Date.now()),
      durationSeconds: durationSec,
      durationFormatted: durationHuman,
      status: "Completed",
    };

    setBreakHistory((prev) => {
      const filtered = (prev || []).filter((item) => item.id !== completedBreak.id);
      const updated = [completedBreak, ...filtered];
      localStorage.setItem(getUserScopedStorageKey(STORAGE_BREAKS_KEY), JSON.stringify(updated));
      return updated;
    });

    setCurrentBreak(null);
    currentBreakRef.current = null;
    lastActivityRef.current = Date.now();
    setLiveStatus("ACTIVE", null);

    // Immediately persist to localStorage so a page refresh reflects active work status
    try {
      const currentUsername = getCurrentLoggedInUsername();
      const stateSnapshot = {
        isShiftActive: true,
        systemTrackingEnabled: true,
        sessionStartTime: sessionStartTimeRef.current || Date.now(),
        activeSeconds: activeSecondsRef.current || 0,
        inactiveSeconds: inactiveSecondsRef.current || 0,
        breakSeconds: breakSecondsRef.current || 0,
        screenLockSeconds: screenLockSecondsRef.current || 0,
        sessionSeconds: sessionSecondsRef.current || 0,
        status: "ACTIVE",
        inactiveReason: null,
        currentBreak: null,
        lastSavedTime: Date.now(),
        lastEndedShift: null,
      };
      localStorage.setItem(getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername), JSON.stringify(stateSnapshot));
    } catch (e) { }

    addToast("success", "Break Ended", `Resumed shift after ${durationHuman} break.`);
    addActivityLog("BREAK_END", "Break Ended", `Resumed active shift after ${durationHuman} of ${b.type} (Reason: ${b.reason || b.type}).`);
    addActivityLog("ACTIVE", "Screen Active", "Break ended - resumed active work on laptop.");
  };

  // Evaluate and apply shift status based on server response (desklogintime and desklogouttime)
  const applyServerShiftResponse = (serverRes) => {
    if (!serverRes) return { hasRecord: false, isShiftActive: false, isShiftEnded: false };

    const resObj = serverRes?.data?.data !== undefined ? serverRes.data : serverRes;
    const record = resObj?.data || resObj;

    const loginTimeRaw = record?.desklogintime;
    const logoutTimeRaw = record?.desklogouttime;

    const hasLoginTime =
      loginTimeRaw &&
      loginTimeRaw !== "0" &&
      loginTimeRaw !== 0 &&
      String(loginTimeRaw).trim() !== "" &&
      String(loginTimeRaw).trim() !== "0";

    const isLogoutEnded =
      logoutTimeRaw &&
      logoutTimeRaw !== "0" &&
      logoutTimeRaw !== 0 &&
      String(logoutTimeRaw).trim() !== "" &&
      String(logoutTimeRaw).trim() !== "0";

    // CASE 1: Shift has ALREADY ENDED today (desklogouttime is NOT "0")
    if (isLogoutEnded) {
      const loginTs = new Date(loginTimeRaw).getTime() || Date.now();
      const loginIso = getIsoDateStr(loginTs);
      const todayIso = getIsoDateStr(Date.now());
      if (loginIso !== todayIso) {
        return {
          hasRecord: true,
          isShiftActive: false,
          isShiftEnded: false,
          record,
        };
      }
      let logoutStr = String(logoutTimeRaw).replace(" ", "T");
      const offsetMatch = String(loginTimeRaw).match(/([+-]\d{2}:\d{2}|Z)$/);
      if (offsetMatch && !logoutStr.match(/([+-]\d{2}:\d{2}|Z)$/)) {
        logoutStr += offsetMatch[1];
      }
      const logoutTs = new Date(logoutStr).getTime() || Date.now();
      const totalSec = Math.max(0, Math.floor((logoutTs - loginTs) / 1000));
      const loginFormatted = formatTimeStr(loginTs);
      const logoutFormatted = formatTimeStr(logoutTs) || String(logoutTimeRaw);

      // Direct screen active time from API / desktop companion agent record
      const rawScreenActive =
        record.screenactivetime !== undefined && record.screenactivetime !== null
          ? record.screenactivetime
          : record.screenActiveTime !== undefined && record.screenActiveTime !== null
            ? record.screenActiveTime
            : record.screen_active_time !== undefined && record.screen_active_time !== null
              ? record.screen_active_time
              : record.desktotalscreenactivetime !== undefined && record.desktotalscreenactivetime !== null
                ? record.desktotalscreenactivetime
                : record.desktotalScreenactivetime !== undefined && record.desktotalScreenactivetime !== null
                  ? record.desktotalScreenactivetime
                  : record.desktotalactiveworktime !== undefined && record.desktotalactiveworktime !== null
                    ? record.desktotalactiveworktime
                    : null;

      const workedSec = parseScreenActiveTimeToSeconds(rawScreenActive);

      setIsShiftActive(false);
      isShiftActiveRef.current = false;
      setSystemTrackingEnabled(false);
      systemTrackingEnabledRef.current = false;
      setStatus("INACTIVE");
      statusRef.current = "INACTIVE";
      setInactiveReason("SHIFT_ENDED");
      inactiveReasonRef.current = "SHIFT_ENDED";
      setCurrentBreak(null);
      currentBreakRef.current = null;
      setSessionStartTime(loginTs);
      sessionStartTimeRef.current = loginTs;

      const idleSec = parseDurationToSeconds(record.desktotalidletime);
      const breakSec = parseBreakTimeToSeconds(record.breaktime);
      const screenLockSec = parseDurationToSeconds(record.desktotalScreenlocktime);
      const totalShiftSec = Math.max(totalSec, workedSec + idleSec + screenLockSec + breakSec);

      // Retain accumulated work on screen - DO NOT wipe to zero!
      setActiveSeconds(workedSec);
      activeSecondsRef.current = workedSec;
      setInactiveSeconds(idleSec);
      inactiveSecondsRef.current = idleSec;
      setBreakSeconds(breakSec);
      breakSecondsRef.current = breakSec;
      setScreenLockSeconds(screenLockSec);
      screenLockSecondsRef.current = screenLockSec;
      setSessionSeconds(totalShiftSec);
      sessionSecondsRef.current = totalShiftSec;

      // Record in attendance history as Completed for today
      const todayStr = formatDateStr(loginTs);
      const shiftIso = loginIso;
      const shiftRecord = {
        id: `ATT-${record.id ? String(record.id).slice(-4) : Date.now().toString().slice(-4)}`,
        date: todayStr,
        isoDate: shiftIso,
        timestamp: loginTs,
        loginTime: loginFormatted,
        logoutTime: logoutFormatted,
        screenActiveTime: workedSec,
        screenactivetime: String(record.screenactivetime !== undefined && record.screenactivetime !== null ? record.screenactivetime : workedSec),
        activeSeconds: workedSec,
        inactiveSeconds: idleSec,
        idleSeconds: idleSec,
        idleTime: formatDuration(record.desktotalidletime || idleSec),
        desktotalidletime: String(record.desktotalidletime !== undefined && record.desktotalidletime !== null ? record.desktotalidletime : idleSec),
        screenLockSeconds: screenLockSec,
        screenLockTime: formatDuration(record.desktotalScreenlocktime || screenLockSec),
        desktotalScreenlocktime: String(record.desktotalScreenlocktime || screenLockSec || "0"),
        breakSeconds: breakSec,
        breakTime: formatBreakTimeMinutes(record.breaktime || breakSec),
        breaktime: String(record.breaktime !== undefined && record.breaktime !== null ? record.breaktime : breakSec),
        totalSeconds: totalShiftSec,
        status: "Completed",
      };

      setAttendanceHistory((prev) => {
        const filtered = prev.filter((r) => r.date !== todayStr && r.isoDate !== shiftIso);
        return [shiftRecord, ...filtered];
      });

      localStorage.setItem(getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY), JSON.stringify(shiftRecord));

      localStorage.setItem(
        getUserScopedStorageKey(STORAGE_STATE_KEY),
        JSON.stringify({
          isShiftActive: false,
          systemTrackingEnabled: false,
          sessionStartTime: loginTs,
          activeSeconds: workedSec,
          inactiveSeconds: idleSec,
          breakSeconds: breakSec,
          screenLockSeconds: screenLockSec,
          sessionSeconds: totalShiftSec,
          status: "INACTIVE",
          inactiveReason: "SHIFT_ENDED",
          currentBreak: null,
          lastEndedShift: shiftRecord,
        })
      );

      addToast("warning", "Shift Already Ended", `Your shift was already completed today (${loginFormatted} - ${logoutFormatted}). Total active: ${formatDurationHuman(workedSec)}.`);
      addActivityLog(
        "LOGOUT",
        "Shift Already Ended",
        `Server indicates shift was completed today: Login: ${loginFormatted}, Logout: ${logoutFormatted}. Active: ${formatDurationHuman(workedSec)}.`
      );

      return {
        hasRecord: true,
        isShiftActive: false,
        isShiftEnded: true,
        loginTime: loginFormatted,
        logoutTime: logoutFormatted,
        record,
      };
    }

    // CASE 2: Shift is ALREADY STARTED and STILL RUNNING (desklogouttime is "0")
    if (hasLoginTime) {
      const loginTs = new Date(loginTimeRaw).getTime() || Date.now();
      const loginIso = getIsoDateStr(loginTs);
      const todayIso = getIsoDateStr(Date.now());
      if (loginIso !== todayIso) {
        return {
          hasRecord: true,
          isShiftActive: false,
          isShiftEnded: false,
          record,
        };
      }
      const now = Date.now();
      const loginFormatted = formatTimeStr(loginTs);
      const savedSession = getSavedSession();

      const rawScreenActive =
        record.screenactivetime !== undefined && record.screenactivetime !== null
          ? record.screenactivetime
          : record.screenActiveTime !== undefined && record.screenActiveTime !== null
            ? record.screenActiveTime
            : record.desktotalscreenactivetime !== undefined && record.desktotalscreenactivetime !== null
              ? record.desktotalscreenactivetime
              : null;
      const parsedRecordScreenActive = rawScreenActive ? parseScreenActiveTimeToSeconds(rawScreenActive) : 0;
      const recordIdle = record.desktotalidletime ? parseDurationToSeconds(record.desktotalidletime) : 0;
      const recordBreak = record.breaktime ? parseBreakTimeToSeconds(record.breaktime) : 0;
      const recordScreenLock = record.desktotalScreenlocktime ? parseDurationToSeconds(record.desktotalScreenlocktime) : 0;
      const elapsedTotal = Math.max(0, Math.floor((now - loginTs) / 1000));

      const finalActive = parsedRecordScreenActive;
      const finalInactive = recordIdle;
      const finalBreak = Math.max(
        Number(savedSession?.breakSeconds) || 0,
        Number(breakSecondsRef.current) || 0,
        Number(breakSeconds) || 0,
        recordBreak
      );
      const finalScreenLock = Math.max(
        Number(savedSession?.screenLockSeconds) || 0,
        Number(screenLockSecondsRef.current) || 0,
        Number(screenLockSeconds) || 0,
        recordScreenLock
      );
      const finalTotal = Math.max(
        Number(savedSession?.sessionSeconds) || 0,
        Number(sessionSecondsRef.current) || 0,
        Number(sessionSeconds) || 0,
        elapsedTotal,
        finalActive + finalInactive + finalScreenLock + finalBreak
      );

      setSessionStartTime(loginTs);
      sessionStartTimeRef.current = loginTs;
      setIsShiftActive(true);
      isShiftActiveRef.current = true;
      setSystemTrackingEnabled(true);
      systemTrackingEnabledRef.current = true;
      setLiveStatus("ACTIVE", null);
      setCurrentBreak(null);
      currentBreakRef.current = null;
      setActiveSeconds(finalActive);
      activeSecondsRef.current = finalActive;
      setInactiveSeconds(finalInactive);
      inactiveSecondsRef.current = finalInactive;
      setBreakSeconds(finalBreak);
      breakSecondsRef.current = finalBreak;
      setScreenLockSeconds(finalScreenLock);
      screenLockSecondsRef.current = finalScreenLock;
      setSessionSeconds(finalTotal);
      sessionSecondsRef.current = finalTotal;
      lastActivityRef.current = now;
      lastTickRef.current = now;

      localStorage.setItem(
        getUserScopedStorageKey(STORAGE_STATE_KEY),
        JSON.stringify({
          isShiftActive: true,
          systemTrackingEnabled: true,
          sessionStartTime: loginTs,
          activeSeconds: finalActive,
          inactiveSeconds: finalInactive,
          breakSeconds: finalBreak,
          screenLockSeconds: finalScreenLock,
          sessionSeconds: finalTotal,
          status: "ACTIVE",
          inactiveReason: null,
          currentBreak: null,
          lastSavedTime: Date.now(),
        })
      );
      localStorage.removeItem(getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY));

      if (typeof window !== "undefined" && "IdleDetector" in window) {
        requestSystemIdleTracking().catch(() => { });
      }

      addToast("info", "Shift Resumed", `Shift already started at ${loginFormatted}. Resumed tracking from that time.`);
      addActivityLog(
        "LOGIN",
        "Shift Resumed",
        `Resumed active shift originally started at ${loginFormatted}. Total elapsed: ${formatDurationHuman(finalActive)}.`
      );

      return {
        hasRecord: true,
        isShiftActive: true,
        isShiftEnded: false,
        loginTime: loginFormatted,
        logoutTime: null,
        record,
      };
    }

    // CASE 3: Not started
    return {
      hasRecord: false,
      isShiftActive: false,
      isShiftEnded: false,
      record,
    };
  };

  // Start New Morning Shift with server verification
  const startShift = async (customMorningTime = null) => {
    const currentUsername = getCurrentLoggedInUsername();
    // When user starts shift, remove the explicit shift ended marker for this user
    localStorage.removeItem(getUserScopedStorageKey(STORAGE_EXPLICIT_END_KEY, currentUsername));
    localStorage.removeItem(getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, currentUsername));

    const pkUserId =
      sessionStorage.getItem("pkUserId") ||
      employee?.id ||
      "USER001";

    const deskUsername = (
      sessionStorage.getItem("userName") ||
      employee?.userName ||
      sessionStorage.getItem("firstName") ||
      employee?.firstName ||
      "Employee"
    ).trim();

    const desklogintime = formatIsoWithOffset(new Date());

    const deskPayload = {
      pk_user_id: pkUserId,
      deskusername: deskUsername,
      desklogintime: desklogintime,
      desklogouttime: "0",
      screenactivetime: "0",
      desktotalScreenlocktime: "0",
      desktotalidletime: "0",
    };

    let serverRes = null;
    try {
      console.log("Calling /User/adddestDetails on startShift with payload:", deskPayload);
      const response = await postAddDeskDetails(deskPayload);
      console.log("adddestDetails API success:", response?.data);
      serverRes = response?.data;
    } catch (apiError) {
      console.error("adddestDetails API error:", apiError);
    }

    const morningStart = customMorningTime || Date.now();
    const now = Date.now();

    setSessionStartTime(morningStart);
    sessionStartTimeRef.current = morningStart;
    setIsShiftActive(true);
    isShiftActiveRef.current = true;
    setSystemTrackingEnabled(true);
    systemTrackingEnabledRef.current = true;
    setLiveStatus("ACTIVE", null);
    setCurrentBreak(null);
    currentBreakRef.current = null;

    setActiveSeconds(0);
    activeSecondsRef.current = 0;
    setInactiveSeconds(0);
    inactiveSecondsRef.current = 0;
    setBreakSeconds(0);
    breakSecondsRef.current = 0;
    setSessionSeconds(0);
    sessionSecondsRef.current = 0;

    lastActivityRef.current = now;
    lastTickRef.current = now;

    const stateSnapshot = {
      isShiftActive: true,
      systemTrackingEnabled: true,
      sessionStartTime: morningStart,
      activeSeconds: 0,
      inactiveSeconds: 0,
      breakSeconds: 0,
      sessionSeconds: 0,
      status: "ACTIVE",
      inactiveReason: null,
      currentBreak: null,
      lastSavedTime: now,
    };
    localStorage.setItem(getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername), JSON.stringify(stateSnapshot));
    localStorage.removeItem(getUserScopedStorageKey(STORAGE_LAST_SHIFT_KEY, currentUsername));

    if (typeof window !== "undefined" && "IdleDetector" in window) {
      requestSystemIdleTracking().catch(() => { });
    }

    addToast("success", "Shift Started", `Shift officially started at ${formatTimeStr(morningStart)}. Active tracking started.`);
    addActivityLog(
      "LOGIN",
      "Shift Started",
      `New shift started at ${formatTimeStr(morningStart)}. Active employee monitoring & desktop sensor enabled.`
    );
    addActivityLog(
      "LOGIN",
      "Server Shift Synced",
      `Desk login recorded on server for ${deskUsername} (${pkUserId}) at ${desklogintime}.`
    );

    return serverRes;
  };

  // Shift End / Logout with full shift summary recording
  const endShift = (logoutAfter = false, onLogoutConfirmed = null, customEndTime = null) => {
    const currentUsername = getCurrentLoggedInUsername();
    const todayIso = getIsoDateStr(Date.now());
    localStorage.setItem(getUserScopedStorageKey(STORAGE_EXPLICIT_END_KEY, currentUsername), todayIso);

    const now = customEndTime ? new Date(customEndTime).getTime() : Date.now();
    const currentStart = sessionStartTimeRef.current || sessionStartTime || now;
    const effectiveLogoutTs = Math.max(currentStart, now);

    const currentActive = activeSecondsRef.current || activeSeconds || 0;
    const currentInactive = inactiveSecondsRef.current || inactiveSeconds || 0;
    const currentBreakSec = breakSecondsRef.current || breakSeconds || 0;
    const currentScreenLock = screenLockSecondsRef.current || screenLockSeconds || 0;
    const capElapsed = Math.max(0, Math.floor((effectiveLogoutTs - currentStart) / 1000));
    const finalSession = Math.max(
      sessionSecondsRef.current || sessionSeconds || 0,
      capElapsed,
      currentActive + currentInactive + currentScreenLock + currentBreakSec
    );

    const shiftRecord = {
      id: `ATT-${Date.now().toString().slice(-4)}`,
      date: formatDateStr(currentStart),
      isoDate: getIsoDateStr(currentStart),
      timestamp: currentStart,
      loginTime: formatTimeStr(currentStart),
      logoutTime: formatTimeStr(effectiveLogoutTs),
      screenActiveTime: currentActive,
      screenactivetime: String(currentActive),
      activeSeconds: currentActive,
      inactiveSeconds: currentInactive,
      idleSeconds: currentInactive,
      idleTime: formatDuration(currentInactive),
      desktotalidletime: String(currentInactive),
      screenLockSeconds: currentScreenLock,
      screenLockTime: formatDuration(currentScreenLock),
      desktotalScreenlocktime: String(currentScreenLock),
      breakSeconds: currentBreakSec,
      breakTime: formatBreakTimeMinutes(currentBreakSec),
      breaktime: String(currentBreakSec),
      totalSeconds: finalSession,
      status: "Completed",
    };

    // Save final shift to Attendance History and last completed shift cache for this user
    const todayStr = formatDateStr(currentStart);
    const shiftIso = getIsoDateStr(currentStart);
    const updatedHistory = [
      shiftRecord,
      ...attendanceHistory.filter((r) => r.date !== todayStr && r.isoDate !== shiftIso),
    ];
    setAttendanceHistory(updatedHistory);

    // Record Logout activity log
    addActivityLog(
      "LOGOUT",
      "Shift Ended",
      `Session completed. Login: ${formatTimeStr(currentStart)}, Logout: ${formatTimeStr(effectiveLogoutTs)}. Active: ${formatDurationHuman(currentActive)}, Idle: ${formatDurationHuman(currentInactive)}, Break: ${formatDurationHuman(currentBreakSec)}`
    );

    setIsShiftActive(false);
    isShiftActiveRef.current = false;
    setSystemTrackingEnabled(false);
    systemTrackingEnabledRef.current = false;
    setStatus("INACTIVE");
    statusRef.current = "INACTIVE";
    setInactiveReason("SHIFT_ENDED");
    inactiveReasonRef.current = "SHIFT_ENDED";
    setCurrentBreak(null);
    currentBreakRef.current = null;

    // Retain accumulated work for today on screen instead of wiping to 0
    setActiveSeconds(currentActive);
    activeSecondsRef.current = currentActive;
    setInactiveSeconds(currentInactive);
    inactiveSecondsRef.current = currentInactive;
    setBreakSeconds(currentBreakSec);
    breakSecondsRef.current = currentBreakSec;
    setScreenLockSeconds(currentScreenLock);
    screenLockSecondsRef.current = currentScreenLock;
    setSessionSeconds(finalSession);
    sessionSecondsRef.current = finalSession;
    setSessionStartTime(currentStart);
    sessionStartTimeRef.current = currentStart;

    // Save state snapshot in localStorage retaining the accumulated time
    const stateSnapshot = {
      isShiftActive: false,
      systemTrackingEnabled: false,
      sessionStartTime: currentStart,
      activeSeconds: currentActive,
      inactiveSeconds: currentInactive,
      breakSeconds: currentBreakSec,
      screenLockSeconds: currentScreenLock,
      sessionSeconds: finalSession,
      status: "INACTIVE",
      inactiveReason: "SHIFT_ENDED",
      currentBreak: null,
      lastEndedShift: shiftRecord,
    };
    localStorage.setItem(getUserScopedStorageKey(STORAGE_STATE_KEY, currentUsername), JSON.stringify(stateSnapshot));

    addToast("info", "Shift Ended", `Shift ended. Total active work today: ${formatDurationHuman(currentActive)}.`);

    if (logoutAfter && typeof onLogoutConfirmed === "function") {
      onLogoutConfirmed();
    }
  };

  const endShiftAndLogout = (onLogoutConfirmed) => {
    endShift(true, onLogoutConfirmed);
  };

  // Export Attendance to CSV
  const exportAttendanceCSV = (customList = null) => {
    const listToExport = customList || attendanceHistory;
    if (!listToExport || listToExport.length === 0) {
      addToast("warning", "No records", "No attendance records available to export.");
      return;
    }

    const headers = [
      "Record ID",
      "Employee ID",
      "Employee Name",
      "Date",
      "Login Time",
      "Logout Time",
      "Active Duration",
      "Inactive Duration",
      "Break Duration",
      "Total Session Duration",
      "Status",
    ];

    const rows = listToExport.map((item) => [
      `"${item.id}"`,
      `"${employee.id}"`,
      `"${employee.name}"`,
      `"${item.date}"`,
      `"${item.loginTime || "--"}"`,
      `"${item.logoutTime || "--"}"`,
      `"${formatDuration(item.activeSeconds)}"`,
      `"${formatDuration(item.inactiveSeconds)}"`,
      `"${formatDuration(item.breakSeconds)}"`,
      `"${formatDuration(item.totalSeconds)}"`,
      `"${item.status}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Petropulse_Attendance_Report_${employee.id}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addToast("success", "Exported Successfully", "Attendance history CSV has been downloaded.");
  };

  // Export Activity Logs to CSV
  const exportActivityLogsCSV = () => {
    if (!activityLogs || activityLogs.length === 0) {
      addToast("warning", "No logs", "No activity logs available to export.");
      return;
    }

    const headers = ["Log ID", "Employee ID", "Timestamp", "Event Type", "Event Title", "Details"];
    const rows = activityLogs.map((log) => [
      `"${log.id}"`,
      `"${employee.id}"`,
      `"${log.timeStr} (${formatDateStr(log.timestamp)})"`,
      `"${log.type}"`,
      `"${log.title}"`,
      `"${log.details.replace(/"/g, '""')}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Petropulse_Activity_Logs_${employee.id}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addToast("success", "Logs Exported", "Activity logs CSV has been downloaded.");
  };

  // Clear Activity Logs
  const clearActivityLogs = () => {
    const currentUsername = getCurrentLoggedInUsername();
    setActivityLogs([]);
    localStorage.removeItem(getUserScopedStorageKey(STORAGE_LOGS_KEY, currentUsername));
    addToast("info", "Logs Cleared", "Activity logs have been cleared.");
  };

  const todayCompletedRecord = getTodayCompletedMetrics();
  const isShiftEndedToday =
    !isShiftActive &&
    (isShiftExplicitlyEndedToday() || (inactiveReason === "SHIFT_ENDED" && Boolean(todayCompletedRecord)));

  return (
    <ActivityTrackerContext.Provider
      value={{
        employee,
        setEmployee,
        status,
        inactiveReason,
        activeSeconds,
        inactiveSeconds,
        breakSeconds,
        screenLockSeconds,
        setScreenLockSeconds,
        sessionSeconds,
        currentBreak,
        sessionStartTime,
        attendanceHistory,
        breakHistory,
        activityLogs,
        isTestMode,
        setIsTestMode,
        isShiftActive,
        isShiftEndedToday,
        isShiftExplicitlyEndedToday,
        STORAGE_EXPLICIT_END_KEY,
        todayCompletedRecord,
        getTodayCompletedMetrics,
        startShift,
        endShift,
        startBreak,
        endBreak,
        endShiftAndLogout,
        getMorningShiftStartTime,
        getShiftCutoffTime,
        formatIsoWithOffset,
        exportAttendanceCSV,
        exportActivityLogsCSV,
        clearActivityLogs,
        toasts,
        dismissToast,
        addToast,
        systemTrackingSupported,
        systemTrackingEnabled,
        requestSystemIdleTracking,
        applyServerShiftResponse,
        fetchAttendanceHistory,
        isLoadingAttendance,
        parseDurationToSeconds,
        parseScreenActiveTimeToSeconds,
        parseBreakTimeToSeconds,
        formatBreakTimeMinutes,
        transformServerDeskRecord,
        getCurrentLoggedInUsername,
        getUserScopedStorageKey,
      }}
    >
      {children}
    </ActivityTrackerContext.Provider>
  );
};

export const useActivityTracker = () => {
  const context = useContext(ActivityTrackerContext);
  if (!context) {
    throw new Error("useActivityTracker must be used within an ActivityTrackerProvider");
  }
  return context;
};
